<template>
  <v-app style="touch-action: manipulation !important">
    <!-- touch-action: manipulation - disable double tap-to-zoom on mobile devices, keeps pinch/pan gestures -->
    <AppBar
      v-if="isAuthenticated"
      :downloading-update="isDownloadingUpdate"
      :update-exists="updateExists"
      :alerts="unreadNotifications.length"
      :mini-nav="miniNav"
      @toggle-nav="miniNav = !miniNav"
      @logout="logout"
      @install-update="refreshApp"
      ref="appBar"
    />

    <!-- Navigation -->
    <v-navigation-drawer
      v-if="isAuthenticated"
      app
      bottom
      :mini-variant.sync="miniNav"
      :mini-variant-width="60"
      :permanent="permanentNav"
      v-model="drawer"
      mobile-breakpoint="960"
      class="background"
      :class="isPWA ? 'pb-5' : ''"
      ref="mainNav"
      @input="(value) => (focusedIndex = null)"
    >
      <v-fade-transition leave-absolute>
        <v-list-item v-if="miniNav && $vuetify.breakpoint.smAndUp" class="px-2">
          <v-list-item-avatar tile class="mt-3 rounded-lg">
            <v-img
              lazy-src="/img/logo_filled.svg"
              max-height="52"
              max-width="150px"
              src="/img/logo_filled.svg"
              aspect-ratio="1"
            ></v-img>
          </v-list-item-avatar>
          <v-list-item-title>mytrace</v-list-item-title>
        </v-list-item>
        <v-list-item
          v-else-if="$vuetify.breakpoint.smAndUp"
          style="max-width: 100%"
          class="d-flex justify-center mt-1"
        >
          <div style="position: relative; width: 100%">
            <div
              class="d-flex justify-center"
              style="
                width: 100% !important;
                background: rgb(78, 172, 255) !important;
                background: linear-gradient(
                  135deg,
                  rgba(78, 172, 255, 1) 50%,
                  rgba(19, 120, 253, 1) 100%
                ) !important;
              "
            >
              <v-img
                lazy-src="/img/wordmark_filled.svg"
                max-height="52"
                max-width="150px"
                src="/img/wordmark_filled.svg"
                aspect-ratio="1"
              ></v-img>
            </div>
          </div>
        </v-list-item>
      </v-fade-transition>

      <v-list-item v-if="miniNav && $vuetify.breakpoint.smAndUp" class="px-2">
        <v-list-item-avatar v-if="miniNav" tile class="rounded-lg">
          <v-btn large icon @click="handleSearch" class="rounded-lg">
            <v-icon color="icon">mdi-magnify</v-icon></v-btn
          >
        </v-list-item-avatar>

        <v-list-item-title>Search</v-list-item-title>
      </v-list-item>

      <v-text-field
        v-else-if="!miniNav"
        placeholder="Search Views"
        hide-details
        rounded
        filled
        dense
        clearable
        full-width
        class="mb-3 px-3 nav-search"
        :class="deviceSize.mobile ? 'mt-3' : 'mt-1'"
        prepend-inner-icon="mdi-magnify"
        v-model="payload.search.views"
        ref="viewSearch"
        @input="handleViewSearch"
        @click:clear="handleViewSearch"
        @keydown.esc="
          $refs.viewSearch.$el.blur();
          drawer = !drawer;
          expandNavOnHover = $vuetify.breakpoint.smAndUp;
        "
        @keydown="
          handleKeyboardNavigation({ text: 'Search', value: 'search' }, $event)
        "
        @focus="
          focusedIndex = getViewIndex({ text: 'Search', value: 'search' })
        "
        tabindex="0"
      ></v-text-field>
      <!-- Home -->
      <v-list-item
        dense
        to="/"
        @keydown="
          handleKeyboardNavigation(
            { text: 'Home', value: 'home', route: '/' },
            $event,
          )
        "
        ref="navItem-home"
        key="home"
        @focus="focusedIndex = getViewIndex({ text: 'Home', value: 'home' })"
        tabindex="0"
        selectable
        @keydown.esc="
          $refs.viewSearch.$el.blur();
          drawer = !drawer;
        "
        @click.stop
      >
        <v-list-item-icon :class="{ 'mr-4': !miniNav }">
          <v-icon color="icon">mdi-home</v-icon>
        </v-list-item-icon>
        <v-list-item-title>
          <span
            class="text-capitalize primary--text font-weight-bold"
            style="font-size: 14px"
            >home</span
          >
        </v-list-item-title>
      </v-list-item>
      <!-- User Views -->
      <v-list
        v-if="getUser && getUser.accountType >= 2"
        nav
        dense
        :expand="payload.search.views?.length ? true : false"
      >
        <v-list-group
          color="review"
          v-for="(phase, phaseIndex) in filteredViews"
          :key="phaseIndex"
          v-model="navGroups[phaseIndex]"
          @keydown="handleKeyboardNavigation(phase, $event)"
          @keydown.esc="
            $refs.viewSearch.$el.blur();
            drawer = !drawer;
          "
          ref="navGroups"
        >
          <template v-slot:activator>
            <v-list-item-icon :class="{ 'mr-4': !miniNav }">
              <v-icon color="icon">{{ phase.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <span
                class="text-uppercase font-weight-bold"
                style="font-size: 14px"
                v-html="matchViewName(phase.text)"
              ></span>
            </v-list-item-title>
          </template>
          <v-list-item
            :to="view.route"
            v-for="(view, viewIndex) in filteredPhaseViews(phase)"
            :key="viewIndex"
            @keydown="handleKeyboardNavigation(view, $event)"
            @keydown.esc="
              $refs.viewSearch.$el.blur();
              drawer = !drawer;
            "
            @click="
              payload.search.views = '';
              blurViewSearch();
            "
            @focus="focusedIndex = getViewIndex(view)"
            ref="navItem"
            class="pr-4 ml-4"
            tabindex="0"
          >
            <v-list-item-icon :class="{ 'mr-4': !miniNav }">
              <v-icon color="icon">{{ view.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <span class="text-wrap" v-html="matchViewName(view.text)"></span>
            </v-list-item-title>
          </v-list-item>
          <v-divider />
        </v-list-group>
        <v-list-item
          v-for="(phase, phaseIndex) in filteredRoutes"
          :key="phase.value"
          @keydown="handleKeyboardNavigation(phase, $event)"
          @focus="focusedIndex = getViewIndex(phase)"
          ref="navItem"
          :to="phase.route"
          :value="phase.value"
          tabindex="0"
          @keydown.esc="
            $refs.viewSearch.$el.blur();
            drawer = !drawer;
          "
        >
          <v-list-item-icon :class="{ 'mr-4': !miniNav }">
            <v-icon color="icon">{{ phase.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-title>
            <span
              class="text-uppercase font-weight-bold"
              style="font-size: 14px"
              v-html="matchViewName(phase.text)"
            ></span>
          </v-list-item-title>
        </v-list-item>
        <template v-if="!filteredViews.length && !filteredRoutes.length">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-subtitle class="text-center">
                <span class="text-wrap">No results found.</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
        <!-- Trace Reports Dropdown -->
        <v-list-group
          v-if="filteredReportCategories.length > 0"
          color="review"
          v-model="reportsNavGroup"
          @keydown="
            handleKeyboardNavigation(
              { text: 'Trace Reports', value: 'reports-overview' },
              $event,
            )
          "
          @keydown.esc="
            $refs.viewSearch.$el.blur();
            drawer = !drawer;
          "
        >
          <template v-slot:activator>
            <v-list-item-icon :class="{ 'mr-4': !miniNav }">
              <v-icon color="icon">mdi-chart-box</v-icon>
            </v-list-item-icon>
            <v-list-item-title>
              <span
                class="text-uppercase font-weight-bold"
                style="font-size: 14px"
                v-html="matchViewName('Trace Reports')"
              ></span>
            </v-list-item-title>
          </template>
          <!-- Reports Overview Link - Prominent -->
          <v-list-item
            :to="{ name: 'reports-overview' }"
            @keydown="
              handleKeyboardNavigation(
                {
                  text: 'Reports Overview',
                  value: 'reports-overview',
                  route: '/reporting/overview',
                },
                $event,
              )
            "
            @keydown.esc="
              $refs.viewSearch.$el.blur();
              drawer = !drawer;
            "
            @click="
              payload.search.views = '';
              blurViewSearch();
            "
            @focus="
              focusedIndex = getViewIndex({
                text: 'Reports Overview',
                value: 'reports-overview',
              })
            "
            ref="navItem"
            class="reports-overview-link"
            tabindex="0"
          >
            <v-list-item-icon :class="{ 'mr-4': !miniNav }">
              <v-icon color="primary">mdi-view-grid-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                <span class="text-wrap font-weight-medium">All Reports</span>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                Browse all {{ totalReportsCount }} reports
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="my-2" />
          <!-- Report Categories - Collapsible -->
          <v-list-group
            v-for="(category, catIndex) in filteredReportCategories"
            :key="`category-${catIndex}`"
            :value="reportsCategoryGroups[catIndex]"
            @input="reportsCategoryGroups[catIndex] = $event"
            color="review"
            sub-group
          >
            <template v-slot:activator>
              <v-list-item-icon :class="{ 'mr-4': !miniNav }">
                <v-icon :color="getReportCategoryColor(category.type)">
                  {{ getReportCategoryIcon(category.type) }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <span
                    class="text-capitalize font-weight-medium"
                    v-html="matchViewName(category.type)"
                  ></span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  {{ category.reports.length }} report{{
                    category.reports.length !== 1 ? "s" : ""
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <!-- Individual Reports -->
            <v-list-item
              v-for="(report, reportIndex) in category.reports"
              :key="`report-${catIndex}-${reportIndex}`"
              :to="{ name: report.to }"
              @keydown="handleKeyboardNavigation(report, $event)"
              @keydown.esc="
                $refs.viewSearch.$el.blur();
                drawer = !drawer;
              "
              @click="
                payload.search.views = '';
                blurViewSearch();
                trackReportView(report.to);
              "
              @focus="focusedIndex = getViewIndex(report)"
              ref="navItem"
              class="pr-4 ml-2"
              tabindex="0"
            >
              <v-list-item-icon :class="{ 'mr-4': !miniNav }">
                <v-icon small color="icon">{{
                  report.icon || "mdi-file-chart-outline"
                }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <span
                    class="text-wrap"
                    v-html="matchViewName(report.text)"
                  ></span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="catIndex < filteredReportCategories.length - 1" />
          </v-list-group>
        </v-list-group>
      </v-list>
      <v-divider v-if="miniNav"></v-divider>
      <v-list-item v-if="miniNav && $vuetify.breakpoint.smAndUp" class="px-0">
        <v-list-item-avatar
          v-if="miniNav"
          tile
          style="width: 60px !important; height: 44px"
        >
          <StyledButton
            preset="action-stacked"
            icon="mdi-menu-close"
            color="background"
            textColor="icon"
            :dense="true"
            minWidth="48px"
            height="36px"
            label="Menu"
            rounded="lg"
            :text-transform="true"
            class="px-1"
          />
        </v-list-item-avatar>
      </v-list-item>
    </v-navigation-drawer>
    <!-- Preview -->
    <v-navigation-drawer
      v-model="rightDrawer.active"
      right
      temporary
      app
      bottom
      touchless
      width="500"
      overlay-opacity="0.25"
      class="navDrawer"
      :mobile-breakpoint="600"
    >
      <v-sheet style="position: sticky; top: -1px; z-index: 2">
        <v-list-item
          fixed
          v-if="rightDrawer.active"
          :class="
            getRightDrawer.type === 'notifications' ? 'blue darken-1' : ''
          "
        >
          <v-list-item-avatar class="mr-2" tile>
            <v-icon
              :large="!deviceSize.mobile"
              class="mr-2"
              :color="
                getRightDrawer.type === 'notifications' ? 'white' : undefined
              "
              >{{ rightDrawerIcon }}</v-icon
            >
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title
              class="font-weight-bold"
              :class="[
                getRightDrawer.type === 'notifications' ? 'white--text' : '',
                getRightDrawer.type === 'userAccess' ? 'text-capitalize' : '',
              ]"
              >{{ getRightDrawer.title }}</v-list-item-title
            >
            <v-list-item-subtitle
              v-if="getRightDrawer.type === 'userAccess' && userAccessSubtitle"
              class="d-flex align-center flex-wrap mt-1"
              style="gap: 6px"
            >
              <v-chip
                v-if="userAccessSubtitle.role"
                small
                class="text-capitalize"
              >
                {{ userAccessSubtitle.role }}
              </v-chip>
              <v-chip
                v-if="userAccessSubtitle.accountType"
                small
                class="text-capitalize"
              >
                {{ userAccessSubtitle.accountType }}
              </v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="getRightDrawer.type === 'cart'">
            <v-btn
              icon
              depressed
              large
              color="blue darken-1"
              class="mr-2 d-block"
              @click="handleInvoiceDownload"
              :loading="$refs.OrderCart?.isDownloading || loading.invoice"
              :disabled="!getRightDrawer.data?.products?.length"
            >
              <div class="d-flex flex-column align-center justify-center">
                <v-icon size="20">mdi-file-download</v-icon>
                <span class="font-weight-bold" style="font-size: 8px !important"
                  >Invoice</span
                >
              </div>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-sheet>
      <v-divider />
      <v-container
        fluid
        :class="[
          getRightDrawer.type === 'notifications' ? 'pa-0' : '',
          getRightDrawer.type === 'userAccess'
            ? 'd-flex flex-column user-access-drawer-container'
            : '',
        ]"
        style="min-height: calc(100vh - topPadding)"
      >
        <TaskPreview v-if="getRightDrawer.type === 'task'" />
        <CategoryPreview v-if="getRightDrawer.type === 'category'" />
        <ProductPreview v-if="getRightDrawer.type === 'product'" />
        <VendorOrderPreview v-if="getRightDrawer.type === 'vendorOrder'" />
        <CustomOrderPreview v-if="getRightDrawer.type === 'customOrder'" />
        <RawMaterialOrderPreview
          v-if="getRightDrawer.type === 'rawMaterialOrder'"
        />
        <AccountPreview v-if="getRightDrawer.type === 'account'" />
        <div
          v-if="getRightDrawer.type === 'userAccess'"
          class="user-access-drawer-body flex-grow-1 d-flex flex-column min-height-0"
        >
          <UserAccessPreview />
        </div>
        <SalesOrderPreview
          v-if="getRightDrawer.type === 'salesOrder'"
          @success-snackbar="successSnackbarActivate"
        />
        <SupportTicketPreview v-if="getRightDrawer.type === 'supportTicket'" />
        <BudtenderTrainingPreview
          v-if="getRightDrawer.type === 'budtenderTraining'"
        />
        <template v-if="getRightDrawer.type === 'alerts'">
          <v-card flat width="auto" class="rounded-lg">
            <v-card-text class="pa-0">
              <v-tabs grow v-model="activeTab.alerts">
                <v-tab>
                  <span class="text-uppercase">all</span>
                  <v-badge
                    inline
                    :color="
                      finalizedAlert.length
                        ? $vuetify.theme.currentTheme.primary
                        : $vuetify.theme.currentTheme.ledger
                    "
                    :content="finalizedAlert.length || '0'"
                  ></v-badge>
                </v-tab>
                <v-tab>
                  <span
                    class="text-uppercase font-weight-bold"
                    :style="`color: ${$vuetify.theme.currentTheme.success}`"
                    >complete</span
                  >
                  <v-badge
                    inline
                    :color="
                      completedAlerts.length
                        ? $vuetify.theme.currentTheme.primary
                        : $vuetify.theme.currentTheme.ledger
                    "
                    :content="completedAlerts.length || '0'"
                  ></v-badge>
                </v-tab>
                <v-tab>
                  <span
                    class="text-uppercase font-weight-bold"
                    :style="`color: ${$vuetify.theme.currentTheme.review}`"
                    >in-progress</span
                  >
                  <v-badge
                    inline
                    :color="
                      inprogressAlerts.length
                        ? $vuetify.theme.currentTheme.primary
                        : $vuetify.theme.currentTheme.ledger
                    "
                    :content="inprogressAlerts.length || '0'"
                  ></v-badge>
                </v-tab>
              </v-tabs>
              <v-text-field
                label="Search"
                rounded
                background-color="optional"
                clearable
                prepend-inner-icon="mdi-magnify"
                v-model.trim="payload.search.alerts"
              ></v-text-field>
              <v-data-table
                :headers="alertHeaders"
                :search="payload.search.alerts"
                :items="alerts"
                group-by="record"
                sort-by="completed"
                sort-desc
                no-data-text="No alerts"
                no-results-text="Not found"
              >
                <template
                  v-slot:group.header="{
                    group,
                    items,
                    isOpen,
                    toggle,
                    remove,
                    headers,
                  }"
                >
                  <GroupByHeaderRow
                    :headers="headers"
                    :group="group"
                    :items="items"
                    :isOpen="isOpen"
                    :showRemoveIcon="false"
                    @toggle="toggle"
                    @remove="remove"
                  />
                </template>
                <template v-slot:item.number="{ index }">
                  <span>{{ index + 1 }}</span>
                </template>
                <template v-slot:item.watcher="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      <span
                        ><span :class="`text-capitalize`">{{
                          item.condition.fieldDisplay
                        }}</span
                        >:
                        <span class="font-weight-bold">{{
                          item.condition.valueDisplay
                        }}</span></span
                      >
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <span class="text-capitalize">current</span>:
                      {{ item.condition.currentValueDisplay }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:item.complete="{ item }">
                  <template v-if="item.complete">
                    <v-icon color="success">{{
                      $vuetify.icons.values.submit
                    }}</v-icon>
                  </template>
                  <template v-else>
                    <span class="text--disabled">-</span>
                  </template>
                </template>
                <template v-slot:item.notes="{ item }">
                  <template v-if="item.notes.length > 0">
                    <span>{{ item.notes }}</span>
                  </template>
                  <template v-else>
                    <span class="text--disabled">-</span>
                  </template>
                </template>
                <template v-slot:item.completed="{ item }">
                  <template v-if="item.completed">
                    <span>{{
                      item.completed | moment("MM/DD/YY @ hh:mm A")
                    }}</span>
                  </template>
                  <template v-else>
                    <span class="text--disabled">-</span>
                  </template>
                </template>
                <template v-slot:item.delete="{ item }">
                  <v-btn
                    small
                    rounded
                    :style="`background: ${$vuetify.theme.currentTheme.error}; color: ${$vuetify.theme.currentTheme.secondary};`"
                    :loading="loading.submit"
                    @click="traverse('alert-delete', item)"
                  >
                    <span class="mr-1">remove</span>
                    <v-icon>{{ $vuetify.icons.values.cancel }}</v-icon>
                  </v-btn>
                </template>
                <template v-slot:item.view="{ item }">
                  <v-btn
                    small
                    rounded
                    :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
                    @click="traverse('alertRecord-view', item)"
                  >
                    <span class="mr-1">view</span>
                    <v-icon>{{ $vuetify.icons.values.view }}</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </template>
        <PhoneLog v-if="getRightDrawer.type === 'phoneLog'" />
        <Notifications v-if="getRightDrawer.type === 'notifications'" />
        <Helpdesk v-if="getRightDrawer.type === 'helpdesk'" />
        <ActivityReplyDrawer v-if="getRightDrawer.type === 'activityReplies'" />
        <OrderCart
          v-if="getRightDrawer.type === 'cart'"
          :products="getRightDrawer?.data?.products"
          :accountDetails="getRightDrawer?.data?.accountDetails"
          :orderType="getRightDrawer?.data?.orderType"
          :editMode="getRightDrawer?.data?.editCart"
          @cancel-edit="rightDrawer.data.editCart = false"
          @disable-submit-button="
            (val) => (rightDrawer.data.disableSubmission = val)
          "
          @cancel-submit="loading.orderSubmit = false"
          ref="OrderCart"
        />
      </v-container>
      <template v-slot:append v-if="getRightDrawer.type === 'cart'">
        <v-container fluid>
          <v-slide-y-reverse-transition
            leave-absolute
            hide-on-leave
            group
            class="row row--dense d-flex px-0 justify-space-between flex-wrap"
            tag="div"
            style="row-gap: 8px"
          >
            <template
              v-if="
                !getRightDrawer?.data?.editCart &&
                getRightDrawer.data?.products?.length
              "
            >
              <template v-if="getRightDrawer.data?.cartWindow === 0">
                <v-col cols="6" class="py-0" key="editButton">
                  <v-btn
                    color="white"
                    block
                    outlined
                    class="blue--text text--darken-1"
                    @click.native.stop="
                      rightDrawer.active = true;
                      rightDrawer.data.editCart = true;
                    "
                    :disabled="!getRightDrawer?.data?.products?.length"
                  >
                    <v-icon
                      :size="$vuetify.breakpoint.xs ? 14 : 18"
                      class="mr-2"
                      v-text="'mdi-pencil'"
                    />
                    Edit
                  </v-btn>
                </v-col>
                <v-col cols="6" class="py-0" key="checkout">
                  <v-btn
                    color="blue darken-1"
                    :disabled="
                      rightDrawer?.data?.products?.some(
                        (item) => !item.totalQTY,
                      )
                    "
                    block
                    class="white--text"
                    @click.native.stop="
                      rightDrawer.data.cartWindow = 1;
                      rightDrawer.title = 'Checkout';
                      $refs.OrderCart.activeViews = {
                        products: null,
                        approvalConfirmation:
                          rightDrawer.data.accountDetails.brandStats.metrics
                            .balance && !$refs.OrderCart.approvalConfirmation,
                      };
                    "
                  >
                    <v-icon
                      :size="$vuetify.breakpoint.xs ? 14 : 18"
                      class="mr-2"
                      v-text="'mdi-cart-arrow-right'"
                    />
                    Checkout
                  </v-btn>
                </v-col>
                <v-col cols="12" class="py-0" key="saveDraftButton">
                  <v-btn
                    color="white"
                    block
                    text
                    class="blue--text text--darken-1"
                    :disabled="cartSubmitLoading"
                    @click="rightDrawer.active = false"
                  >
                    Close
                  </v-btn>
                </v-col>
              </template>
              <template v-else>
                <v-col cols="6" class="py-0" key="saveDraftButton">
                  <v-btn
                    color="blue darken-1"
                    block
                    outlined
                    :disabled="cartSubmitLoading"
                    :loading="$refs.OrderCart?.loading?.draft"
                    @click="
                      getRightDrawer?.data?.deleteDraftOrders?.length === 1
                        ? $refs.OrderCart.saveDraft(
                            getRightDrawer?.data?.deleteDraftOrders[0].id,
                          )
                        : $refs.OrderCart.saveDraft()
                    "
                  >
                    <v-icon
                      :size="$vuetify.breakpoint.xs ? 14 : 18"
                      class="mr-2"
                    >
                      {{
                        getRightDrawer?.data?.deleteDraftOrders?.length === 1
                          ? "mdi-update"
                          : "mdi-content-save"
                      }}
                    </v-icon>
                    {{
                      getRightDrawer?.data?.deleteDraftOrders?.length === 1
                        ? "Update Draft"
                        : "Save Draft"
                    }}
                  </v-btn>
                </v-col>
                <v-col cols="6" class="py-0" key="submit">
                  <v-btn
                    color="green darken-1"
                    :disabled="
                      !getRightDrawer?.data?.products?.every(
                        (item) =>
                          item.totalQTY > 0 && item.totalQTY <= item.available,
                      ) ||
                      $refs.OrderCart?.productsUnavailable?.length > 0 ||
                      $refs.OrderCart?.invalidSubmission ||
                      $refs.OrderCart?.loading?.draft
                    "
                    block
                    :loading="cartSubmitLoading"
                    class="white--text"
                    @click="handleOrderSubmit"
                  >
                    <v-icon
                      :size="$vuetify.breakpoint.xs ? 14 : 18"
                      class="mr-2"
                      v-text="'mdi-check-circle'"
                    />
                    Submit
                  </v-btn>
                </v-col>
                <v-col cols="12" class="py-0" key="backButton">
                  <v-btn
                    color="white"
                    block
                    text
                    class="blue--text text--darken-1"
                    :disabled="cartSubmitLoading"
                    @click="
                      rightDrawer.data.cartWindow = 0;
                      rightDrawer.title = 'Cart';
                    "
                  >
                    Back
                  </v-btn>
                </v-col>
              </template>
            </template>
            <v-col
              v-else-if="getRightDrawer?.data?.products.length"
              cols="12"
              class="py-0"
              key="button"
            >
              <v-btn
                color="white"
                block
                text
                class="blue--text text--darken-1"
                @click="rightDrawer.data.editCart = false"
              >
                Done
              </v-btn>
            </v-col>
          </v-slide-y-reverse-transition>
        </v-container>
      </template>
    </v-navigation-drawer>
    <!-- Notification, New -->
    <v-dialog
      persistent
      scrollable
      transition="scale-transition"
      v-model="getNotificationDialog"
    >
      <v-card>
        <v-card-title>
          <v-icon
            large
            class="mr-1"
            :color="$vuetify.theme.currentTheme.primary"
            >{{ $vuetify.icons.values.notification }}</v-icon
          >
          <span class="text-uppercase">new notification</span>
        </v-card-title>
        <v-card-text class="my-3">
          <template v-if="getNotificationRecord">
            <!-- New Notification -->
            <v-card flat class="rounded-lg">
              <v-card-text>
                <v-form>
                  <v-row>
                    <v-col cols="12" md="12" sm="12">
                      <v-autocomplete
                        label="Recipient"
                        placeholder="Select User(s)"
                        persistent-placeholder
                        :background-color="
                          notificationAssigneeSet ? '' : 'required'
                        "
                        :loading="getNotificationUsersLoading"
                        multiple
                        :items="getNotificationUsers"
                        no-data-text="No Users"
                        :hint="`${getNotificationUsers.length} user${
                          getNotificationUsers.length > 1 ||
                          getNotificationUsers.length === 0
                            ? 's'
                            : ''
                        } available`"
                        persistent-hint
                        v-model="getNotificationRecord.assignedTo"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <span class="text-capitalize">{{
                                item.text
                              }}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              <span class="text-capitalize">{{
                                item.department
                              }}</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item, index }">
                          <span class="text-capitalize mr-1" v-if="index === 0"
                            >{{ item.text }}
                            <span
                              v-if="getNotificationRecord.assignedTo.length > 1"
                              class="grey--text text-caption"
                              >(+{{
                                getNotificationRecord.assignedTo.length - 1
                              }}
                              others)</span
                            >
                          </span>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="12" sm="12">
                      <v-textarea
                        label="Message"
                        placeholder="Enter message..."
                        persistent-placeholder
                        outlined
                        :background-color="
                          notificationNotesSet ? '' : 'required'
                        "
                        v-model="getNotificationRecord.notes"
                        hide-details
                      ></v-textarea>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </template>
          <template v-else>
            <v-skeleton-loader type="card, divider, text"></v-skeleton-loader>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            :style="`color: ${$vuetify.theme.currentTheme.error};`"
            @click="traverse('notification-cancel')"
            :disabled="loading.submit"
          >
            <span>cancel</span>
          </v-btn>
          <v-spacer />
          <v-btn
            text
            :style="`color: ${$vuetify.theme.currentTheme.success};`"
            :loading="loading.submit"
            @click="traverse('notification-submit')"
            :disabled="invalidNotificationSubmission"
          >
            <span>submit</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Email, New -->
    <v-dialog
      scrollable
      persistent
      transition="scale-transition"
      v-model="getEmailDialog"
    >
      <v-card>
        <v-card-title
          class="justify-center"
          :style="`background: ${$vuetify.theme.currentTheme.primary}; color: ${$vuetify.theme.currentTheme.secondary};`"
        >
          <span class="text-uppercase">email | new</span>
        </v-card-title>
        <v-card-text class="my-3">
          <template v-if="getEmailRecord">
            <v-alert color="ledger" dark>
              <v-icon large class="mr-2">{{
                $vuetify.icons.values.info
              }}</v-icon>
              <span
                >{{ getEmailRecord.related.name }} -
                <span class="text-capitalize">{{
                  getEmailRecord.related.type
                }}</span></span
              >
            </v-alert>
            <!-- Email -->
            <v-form>
              <v-row no-gutters>
                <v-col cols="12" md="6" sm="12">
                  <v-combobox
                    label="To"
                    :background-color="emailToSet ? '' : 'required'"
                    deletable-chips
                    multiple
                    chips
                    :hint="`${getEmailRecord.contacts.length} contact${
                      getEmailRecord.contacts.length > 1 ||
                      getEmailRecord.contacts.length === 0
                        ? 's'
                        : ''
                    } available`"
                    persistent-hint
                    :items="getEmailRecord.contacts"
                    v-model="getEmailRecord.to"
                    @input="cleanInput('to')"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="text-capitalize">{{ item.text }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span class="text-capitalize">{{ item.title }}</span>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <span>{{ item.email }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-combobox>
                </v-col>
                <v-col cols="12" md="3" sm="12">
                  <v-autocomplete
                    label="CC"
                    :loading="getEmailUsersLoading"
                    multiple
                    deletable-chips
                    chips
                    :hint="`${getEmailUsers.length} user${
                      getEmailUsers.length > 1 || getEmailUsers.length === 0
                        ? 's'
                        : ''
                    } available`"
                    persistent-hint
                    :items="getEmailUsers"
                    no-data-text="No Users"
                    v-model="getEmailRecord.cc"
                    @input="cleanInput('cc')"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="text-capitalize">{{ item.text }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span>{{ item.email }}</span>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <span class="text-capitalize">{{
                            item.department
                          }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <span v-if="index === 0" class="mr-1 text-capitalize"
                        >{{ item.text }}
                        <span
                          v-if="getEmailRecord.cc.length > 1"
                          class="grey--text text-caption"
                          >(+{{ getEmailRecord.cc.length - 1 }} others)</span
                        >
                      </span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="3" sm="12">
                  <v-autocomplete
                    label="BCC"
                    :loading="getEmailUsersLoading"
                    multiple
                    deletable-chips
                    chips
                    :hint="`${getEmailUsers.length} user${
                      getEmailUsers.length > 1 || getEmailUsers.length === 0
                        ? 's'
                        : ''
                    } available`"
                    persistent-hint
                    :items="getEmailUsers"
                    no-data-text="No Users"
                    v-model="getEmailRecord.bcc"
                    @input="cleanInput('bcc')"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="text-capitalize">{{ item.text }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span>{{ item.email }}</span>
                        </v-list-item-subtitle>
                        <v-list-item-subtitle>
                          <span class="text-capitalize">{{
                            item.department
                          }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item, index }">
                      <span v-if="index === 0" class="mr-1 text-capitalize"
                        >{{ item.text }}
                        <span
                          v-if="getEmailRecord.bcc.length > 1"
                          class="grey--text text-caption"
                          >(+{{ getEmailRecord.bcc.length - 1 }} others)</span
                        >
                      </span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="6" sm="12">
                  <v-text-field
                    single-line
                    placeholder="Subject"
                    :background-color="emailSubjectSet ? '' : 'required'"
                    v-model.trim="getEmailRecord.subject"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6" sm="12">
                  <v-autocomplete
                    label="Email Templates"
                    :items="getEmailRecord.templates"
                    persistent-hint
                    :hint="`${getEmailRecord.templates.length} template${
                      getEmailRecord.templates.length > 1 ||
                      getEmailRecord.templates.length === 0
                        ? 's'
                        : ''
                    } available`"
                    @click:clear="
                      getEmailRecord.subject = getEmailRecord.body = ''
                    "
                    clearable
                    v-model="getEmailRecord.template"
                    @change="setTemplate"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span class="text-capitalize">{{
                            item.subject
                          }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Updated: {{ item.updated | moment("MM/DD/YY") }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span class="text-capitalize">{{ item.subject }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" md="12" sm="12">
                  <v-textarea
                    outlined
                    :background-color="emailBodySet ? '' : 'required'"
                    placeholder="Enter your message here..."
                    v-model.trim="getEmailRecord.body"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="12" sm="12">
                  <v-btn
                    rounded
                    :style="`background: ${$vuetify.theme.currentTheme.review}; color: ${$vuetify.theme.currentTheme.secondary};`"
                    @click="uploadFile"
                  >
                    <v-icon class="mr-2">mdi-paperclip-plus</v-icon>
                    <span>add attachment(s)</span>
                  </v-btn>
                </v-col>
                <v-col
                  v-if="getEmailRecord.attachments.length > 0"
                  cols="12"
                  md="12"
                  sm="12"
                >
                  <v-col
                    v-for="(
                      attachment, attachmentID
                    ) in getEmailRecord.attachments"
                    :key="attachmentID"
                    cols="auto"
                  >
                    <span class="mr-4">{{ attachment.filename }}</span>
                    <v-btn
                      small
                      rounded
                      :style="`background: ${$vuetify.theme.currentTheme.error}; color: ${$vuetify.theme.currentTheme.secondary};`"
                      @click="removeAttachment(attachment)"
                    >
                      <v-icon class="mr-2">mdi-trash-can</v-icon>
                      <span>remove</span>
                    </v-btn>
                  </v-col>
                </v-col>
              </v-row>
            </v-form>
          </template>
          <template v-else>
            <v-skeleton-loader type="card, divider, text"></v-skeleton-loader>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            small
            rounded
            :style="`background: ${$vuetify.theme.currentTheme.error}; color: ${$vuetify.theme.currentTheme.secondary};`"
            @click="traverse('email-cancel')"
            :disabled="loading.submit || !getEmailRecord"
          >
            <v-icon>{{ $vuetify.icons.values.cancel }}</v-icon>
            <span>cancel</span>
          </v-btn>
          <v-spacer />
          <v-btn
            small
            rounded
            :style="`background: ${$vuetify.theme.currentTheme.success}; color: ${$vuetify.theme.currentTheme.secondary};`"
            :loading="loading.submit"
            @click="traverse('email-submit')"
            :disabled="invalidEmailSubmission || !getEmailRecord"
          >
            <span>submit</span>
            <v-icon>{{ $vuetify.icons.values.submit }}</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
      <!-- Invalid Email entered -->
      <v-snackbar
        centered
        timeout="3000"
        transition="scale-transition"
        color="error"
        v-model="errorSnackbar.active"
      >
        {{ errorSnackbar.msg }}
      </v-snackbar>
    </v-dialog>
    <!-- Action List -->
    <v-dialog
      scrollable
      persistent
      transition="scale-transition"
      v-model="getActionListDialog"
    >
      <ActionItem v-if="getActionListDialog" />
    </v-dialog>
    <!-- Alerts -->
    <v-dialog
      scrollable
      persistent
      transition="scale-transition"
      v-model="getAlertDialog"
    >
      <Alert v-if="getAlertDialog" />
    </v-dialog>
    <!-- App -->
    <v-main class="background">
      <v-container
        fluid
        class="bg pt-0"
        style="height: 100% !important; width: 100% !important"
      >
        <v-row no-gutter dense>
          <v-col cols="12">
            <Breadcrumbs />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <router-view />
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <v-bottom-navigation
      v-if="getUser && deviceSize.mobile"
      color="review"
      v-model="activeView"
      :class="{ 'pb-4': isPWA }"
      :height="
        56 +
        (isPWA ? 20 : 0) +
        (isDownloadingUpdate ? 20 : updateExists ? 52 : 0)
      "
      app
    >
      <v-slide-y-reverse-transition
        tag="div"
        class="d-flex flex-column align-center"
        :class="{
          'justify-center': !updateExists,
          'justify-space-between pt-3': updateExists,
        }"
        style="width: 100%"
        :style="{ gap: updateExists ? '6px' : '0px' }"
        group
      >
        <div
          v-if="isDownloadingUpdate"
          class="text-caption font-weight-bold text--secondary d-flex align-center justify-center"
          style="gap: 6px"
          key="updates"
        >
          <v-progress-circular
            :width="2"
            size="10"
            color="loading"
            indeterminate
          ></v-progress-circular>
          Downloading Updates
        </div>
        <StyledButton
          v-else-if="updateExists"
          preset="outlined"
          label="Install Updates"
          icon="mdi-download"
          iconSize="14px"
          minHeight="36px"
          size="small"
          rounded="pill"
          key="updates-available"
          extraClasses="v-btn--active"
          :action="refreshApp"
        />
        <div
          class="d-flex justify-space-between px-2"
          :class="{ 'align-center': !isPWA, 'align-start': isPWA }"
          style="width: 100%"
          key="tabs"
        >
          <StyledButton
            preset="stacked"
            :icon="activeView === 0 ? 'mdi-home' : 'mdi-home-outline'"
            label="Home"
            rounded="lg"
            :text-transform="true"
            :to="{ name: 'home' }"
            :textColor="activeView === 0 ? 'review' : 'icon'"
            target="self"
            :minHeight="56"
            :ripple="false"
            style="
              width: clamp(80px, 19%, 19%) !important;
              min-width: 0% !important;
            "
          />
          <StyledButton
            preset="stacked"
            :icon="activeView === 1 ? 'mdi-account' : 'mdi-account-outline'"
            label="Accounts"
            rounded="lg"
            :text-transform="true"
            :textColor="activeView === 1 ? 'review' : 'icon'"
            :to="{ name: 'accounts-overview' }"
            target="self"
            :minHeight="56"
            :ripple="false"
            style="
              width: clamp(80px, 19%, 19%) !important;
              min-width: 0% !important;
            "
          />
          <StyledButton
            preset="stacked"
            :textColor="activeView === 2 ? 'review' : 'icon'"
            :icon="
              activeView === 2
                ? 'mdi-clipboard-list'
                : 'mdi-clipboard-list-outline'
            "
            label="Orders"
            rounded="lg"
            :text-transform="true"
            :to="{ name: 'saleOrders-overview' }"
            target="self"
            :minHeight="56"
            :ripple="false"
            style="
              width: clamp(80px, 19%, 19%) !important;
              min-width: 0% !important;
            "
          />
          <StyledButton
            preset="stacked"
            icon="mdi-magnify"
            textColor="icon"
            label="Search"
            rounded="lg"
            :text-transform="true"
            :action="
              () => {
                $refs.appBar.handleSearchClick();
              }
            "
            :minHeight="56"
            :ripple="false"
            style="
              width: clamp(80px, 19%, 19%) !important;
              min-width: 0% !important;
            "
          />
          <StyledButton
            preset="stacked"
            icon="mdi-menu"
            label="More"
            textColor="icon"
            rounded="lg"
            :text-transform="true"
            :action="
              () => {
                drawer = !drawer;
              }
            "
            :minHeight="56"
            :ripple="false"
            style="
              width: clamp(80px, 19%, 19%) !important;
              min-width: 0% !important;
            "
          />
        </div>
      </v-slide-y-reverse-transition>
    </v-bottom-navigation>

    <!-- App Wide Snackbar -->
    <Toast ref="toast" />
    <!-- Keyboard Shortcuts Tutorial Dialog -->
    <v-dialog
      v-model="dialog.keyboardShortcuts"
      width="350"
      overlay-opacity="0"
      content-class="rounded-xl"
    >
      <v-card
        v-if="dialog.keyboardShortcuts"
        class="blurred-dialog rounded-xl"
        :style="{
          background: `${$vuetify.theme.currentTheme.secondary}3D`,
          border: `1px solid ${$vuetify.theme.currentTheme.secondary}C4`,
        }"
      >
        <v-card-title class="headline"
          ><v-icon size="30" class="mr-2">mdi-keyboard</v-icon>
          Shortcuts</v-card-title
        >
        <v-card-text>
          <v-container>
            <v-row
              v-for="(shortcut, index) in keyboardShortcutsArray"
              :key="index"
              dense
            >
              <v-col cols="12" class="d-flex justify-space-between"
                ><span class="font-weight-black text-body-1 text--primary"
                  >{{ shortcut.text }}
                </span>
                <div class="d-flex align-center">
                  <v-icon size="14" class="mr-2" color="primary">{{
                    shortcut.keys[0].icon
                  }}</v-icon>
                  <span
                    class="font-weight-bold text-body-1 text--primary"
                    style="min-width: 24px"
                    >{{ shortcut.keys[1] }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Success Snackbar -->
    <v-snackbar
      :value="successSnackbar.active"
      color="success"
      timeout="2500"
      rounded="lg"
      bottom
    >
      <v-layout align-center justify-space-around>
        <v-icon class="pr-3" dark>mdi-check-circle</v-icon>
        <v-layout column>
          <div class="text-subtitle-1 font-weight-bold">
            {{ successSnackbar.msg }}
          </div>
        </v-layout>
      </v-layout>
    </v-snackbar>
    <v-snackbar
      multi-line
      centered
      rounded="lg"
      transition="fab-transition"
      color="review"
      :value="completedAlerts.length || unreadNotifications.length"
    >
      <v-layout align-center justify-space-around>
        <v-icon class="pr-3" dark>mdi-information</v-icon>
        <v-layout column>
          <div class="text-subtitle-1 font-weight-bold">
            You have
            <template v-if="unreadNotifications.length">
              {{ unreadNotifications.length | addComma }} notification{{
                unreadNotifications.length > 1 ||
                unreadNotifications.length === 0
                  ? "s"
                  : ""
              }}
            </template>
            {{
              unreadNotifications.length && completedAlerts.length ? " & " : ""
            }}
            <template v-if="completedAlerts.length"
              >{{ completedAlerts.length | addComma }} alert{{
                completedAlerts.length > 1 || completedAlerts.length === 0
                  ? "s"
                  : ""
              }}</template
            >
            <v-icon style="rotate: 30deg">mdi-arrow-up</v-icon>
          </div>
        </v-layout>
      </v-layout>
    </v-snackbar>
  </v-app>
</template>

<script>
// Libraries
import { mapActions, mapGetters } from "vuex";
import {
  // Auth
  auth,
  signOut,
  // Firestore
  firestore,
  collection,
  onSnapshot,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  limit,
  query,
  where,
  writeBatch,
  or,
  increment,
  // Storage
  storage,
  ref,
  getDownloadURL,
  // Functions
  functions,
  httpsCallable,
  // Remote Config
  remoteConfig,
  fetchAndActivate,
  getValue,
  // Messaging
  // messaging,
  // getToken,
  // deleteToken,
} from "@/firebase/init";
import moment from "moment-timezone";
// import algoliasearch from "algoliasearch/lite";
import "broadcastchannel-polyfill";
import * as _ from "lodash";

import update from "@/mixins/update";
import ReportTrackingService from "@/utils/ReportTrackingService";

// Components
// Right Drawer
import TaskPreview from "@/components/misc/rightDrawer/TaskPreview.vue";
import CategoryPreview from "@/components/misc/rightDrawer/CategoryPreview.vue";
import ProductPreview from "@/components/misc/rightDrawer/ProductPreview.vue";
import VendorOrderPreview from "@/components/misc/rightDrawer/VendorOrderPreview.vue";
import CustomOrderPreview from "@/components/misc/rightDrawer/CustomOrderPreview.vue";
import RawMaterialOrderPreview from "@/components/misc/rightDrawer/RawMaterialOrderPreview.vue";
import AccountPreview from "@/components/misc/rightDrawer/AccountPreview.vue";
import SalesOrderPreview from "@/components/misc/rightDrawer/SalesOrderPreview.vue";
import OrderCart from "@/components/misc/rightDrawer/SalesOrderCart.vue";
import SupportTicketPreview from "@/components/misc/rightDrawer/SupportTicketPreview.vue";
import BudtenderTrainingPreview from "@/components/misc/rightDrawer/BudtenderTrainingPreview.vue";
import Notifications from "@/components/misc/rightDrawer/Notifications.vue";
import Helpdesk from "@/components/misc/rightDrawer/Helpdesk.vue";
import PhoneLog from "@/components/misc/rightDrawer/PhoneLog.vue";
import ActivityReplyDrawer from "@/components/misc/rightDrawer/ActivityReplyDrawer.vue";
import UserAccessPreview from "@/components/misc/rightDrawer/UserAccessPreview.vue";
import ActionItem from "@/components/misc/ui/ActionItem.vue";
import AccountCell from "@/components/misc/ui/AccountCell.vue";
import Alert from "@/components/misc/ui/Alert.vue";
import StyledButton from "@/components/misc/ui/buttons/StyledButton.vue";
import AppBar from "@/components/misc/ui/navigation/AppBar.vue";
import Breadcrumbs from "@/components/misc/ui/Breadcrumbs.vue";

const themeChannel = new BroadcastChannel("theme-channel");

export default {
  name: "app",
  mixins: [update],
  data: () => ({
    final: [],
    loading: {
      overall: false,
      search: false,
      users: false,
      accounts: false,
      submit: false,
      approveOrder: false,
      algolia: false,
      orderSubmit: false,
      invoice: false,
    },
    dialog: {
      search: false,
      keyboardShortcuts: false,
    },
    activeTab: {
      alerts: 0,
      notifications: 0,
    },
    drawer: false,
    fab: false,
    response: {
      headers: {
        accounts: [
          {
            text: "#",
            value: "number",
            sortable: false,
          },
          {
            text: "Name",
            value: "name",
            sortable: true,
          },
          {
            text: "Last Order",
            value: "lastOrder",
            sortable: true,
          },
          {
            text: "Balance",
            value: "balance",
            sortable: true,
          },
          {
            text: "Total Orders",
            value: "totalOrders",
            sortable: true,
          },
          {
            text: "Status",
            value: "status",
            sortable: true,
          },
          {
            text: "Select",
            value: "select",
            sortable: false,
          },
        ],
        lineItems: [
          { text: "#", value: "number", sortable: false, groupable: false },
          { text: "Name", value: "name", sortable: false, groupable: false },
          { text: "Price", value: "price", sortable: false, groupable: false },
          {
            text: "Units",
            value: "totalUnits",
            sortable: false,
            groupable: false,
            align: "center",
          },
          {
            text: "Total",
            value: "total",
            sortable: false,
            groupable: false,
            align: "center",
          },
        ],
        rawMaterialOrderLineItems: [
          {
            text: "#",
            value: "number",
            sortable: false,
          },

          {
            text: "Raw Material",
            value: "rawMaterial",
            sortable: true,
          },
          {
            text: "Sub-Category",
            value: "subCategory",
            sortable: true,
          },
          {
            text: "Batch Name",
            value: "batchName",
            sortable: true,
          },
          {
            text: "Notes",
            value: "notes",
            sortable: false,
          },
          {
            text: "Amount purchased",
            value: "amount",
            sortable: true,
          },
          {
            text: "Price",
            value: "price",
            sortable: true,
          },
          {
            text: "Total",
            value: "total",
            sortable: true,
          },
          {
            text: "Split",
            value: "split",
            sortable: false,
          },
          {
            text: "Available at location?",
            value: "availableAtLocation",
            sortable: true,
          },
        ],
        products: [
          { text: "#", value: "number", sortable: false },
          { text: "Name", value: "name" },
          { text: "Color", value: "color", sortable: false },
          { text: "Visibility", value: "visibility", sortable: false },
        ],
        vendorOrderProducts: [
          { text: "#", value: "number", sortable: false },
          { text: "Name", value: "name" },
          { text: "SKU", value: "sku" },
          { text: "Full QTY/Custom QTY?", value: "qty" },
        ],
        locations: [
          {
            text: "#",
            value: "number",
            sortable: false,
          },
          {
            text: "Name",
            value: "name",
            sortable: true,
          },
        ],
        variablePricing: [
          { text: "#", value: "number", sortable: false },
          { text: "Term", value: "term" },
          { text: "Wholesale Price", value: "price" },
        ],
        expenses: [
          {
            text: "#",
            value: "number",
            sortable: false,
          },
          {
            text: "Name",
            value: "name",
          },
          {
            text: "State",
            value: "state",
          },
          {
            text: "Location",
            value: "location",
          },
          {
            text: "Cost",
            value: "amount",
          },
          {
            text: "QTY",
            value: "qty",
          },
          {
            text: "Total",
            value: "total",
          },
        ],
        alerts: [],
      },
      views: [],
      // algolia: algoliasearch(
      //   process.env.VUE_APP_ALGOLIA_APPID,
      //   process.env.VUE_APP_ALGOLIA_API
      // ),
      hits: [],
      notifications: [],
      alerts: {
        conditions: [],
        records: [],
      },
      devices: [],
    },
    payload: {
      query: "",
      index: "",
      notification: null,
      filters: {
        brand: null,
      },
      search: {
        accounts: "",
        alerts: "",
        views: "",
      },
    },
    errorSnackbar: {
      active: false,
      msg: "",
    },
    dateAndTime: moment().toDate(),
    menu: false,
    rejectionNotes: "",
    rejectionMenuActive: false,
    successSnackbar: {
      active: false,
      msg: "",
    },
    edit: {
      cart: false,
    },
    fcm: "",
    navGroups: [],
    reportsNavGroup: false,
    reportsCategoryGroups: {},
    shortCutHoldTimers: {
      tutorial: null,
    },
    focusedIndex: -1,
    focusedSearchIndex: -1,
    accountSearch: null,
    microphone: false,
    miniNav: true,
    miniNavKey: 0,
    permanentNav: false,
    searchVisible: false,
    activeView: null,
    snapshots: [],
  }),
  beforeMount() {
    const storageTheme = localStorage.getItem("theme");
    if (storageTheme) {
      this.$vuetify.theme.dark = storageTheme === "dark";
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      metaThemeColor.setAttribute(
        "content",
        this.$vuetify.theme.currentTheme.background,
      );
    } else if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      this.$vuetify.theme.dark = true;
      localStorage.setItem(
        "theme",
        this.$vuetify.theme.dark ? "dark" : "light",
      );
      themeChannel.postMessage({
        theme: this.$vuetify.theme.dark ? "dark" : "light",
      });
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      metaThemeColor.setAttribute(
        "content",
        this.$vuetify.theme.currentTheme.background,
      );
    }
  },
  async mounted() {
    // remoteConfig.settings.minimumFetchIntervalMillis = 10000;
    remoteConfig.defaultConfig = {
      maxUserCount: 20,
      retailBrands: 1,
      whiteLabelBrands: 1,
    };
    await fetchAndActivate(remoteConfig);
    this.$nextTick(() => {
      this.$root.toast = this.$refs.toast;
    });

    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.handleWindowBlur);
    themeChannel.onmessage = (event) => {
      if (event.data.theme !== this.$vuetify.theme.currentTheme) {
        this.$vuetify.theme.dark = event.data.theme === "dark";
      }
    };
    const routes = {
      "/": 0,
      "/accounts/overview": 1,
      "/salesOrders/overview": 3,
    };

    let testPath = (path) => {
      const match = path.match(/(?<=\/)[^\/]+(?=\/)/);
      let foundKey = Object.keys(routes).find(
        (key) => key === path || (match && key.includes(match[0])),
      );
      return foundKey ? routes[foundKey] : null;
    };
    this.$nextTick(() => {
      this.searchVisible = false;
      this.activeView = testPath(this.$route.path) || null;
    });
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("blur", this.handleWindowBlur);
    document.removeEventListener("keyup", this.handleKeyUp);
    this.$cacheManager.stopCleanup();
    themeChannel.close();
  },
  async created() {
    this.setup();
    setInterval(this.getDateAndTime, 1000);
    if (this.deviceSize.mobile) {
      this.miniNav = false;
      this.permanentNav = false;
    } else {
      this.miniNav = true;
      this.permanentNav = true;
    }
  },
  components: {
    TaskPreview,
    CategoryPreview,
    ProductPreview,
    VendorOrderPreview,
    CustomOrderPreview,
    RawMaterialOrderPreview,
    AccountPreview,
    SalesOrderPreview,
    OrderCart,
    SupportTicketPreview,
    BudtenderTrainingPreview,
    Notifications,
    Helpdesk,
    PhoneLog,
    ActivityReplyDrawer,
    UserAccessPreview,
    ActionItem,
    Alert,
    AccountCell,
    StyledButton,
    AppBar,
    Breadcrumbs,
  },
  watch: {
    "$vuetify.breakpoint.smAndUp"(value) {
      this.$nextTick(() => {
        if (this.deviceSize.mobile) {
          this.miniNav = false;
          this.permanentNav = false;
        } else {
          this.permanentNav = true;
          if (!this.drawer) {
            this.miniNav = true;
          }
        }
      });
    },
    miniNav(value) {
      if (value) {
        for (let i = 0; i < this.navGroups.length; i++) {
          this.navGroups[i] = false;
        }
      }
    },
    $route(to) {
      // Update the active tab based on route path
      const routes = {
        "/": 0,
        "/accounts/overview": 1,
        "/salesOrders/overview": 3,
      };

      let testPath = (path) => {
        const match = path.match(/(?<=\/)[^\/]+(?=\/)/);
        let foundKey = Object.keys(routes).find(
          (key) => key === path || (match && key.includes(match[0])),
        );
        return foundKey ? routes[foundKey] : null;
      };
      this.$nextTick(() => {
        if (this.$refs?.appBar) {
          this.$refs.appBar.searchVisible = false;
          this.$refs.appBar.menu = false;
        }
        this.activeView = testPath(to.path) || null;
      });
    },
    activeView(oldValue, newValue) {
      if (![0, 1, 2].includes(newValue)) {
        this.activeView = oldValue;
      }
    },
  },
  methods: {
    ...mapActions([
      "_user",
      "_activityDialog",
      "_notification",
      "_email",
      "_dialpadFeatureFlag",
      "_rightDrawer",
      "_alerts",
    ]),
    /* API */
    // GET
    async getUserMetadata() {
      if (auth.currentUser) {
        const userDoc = doc(firestore, "users", auth.currentUser.uid);
        let snapshot = onSnapshot(userDoc, (response) => {
          if (!response.empty) {
            this._user({
              uid: response.id,
              ...response.data(),
              initials: auth.currentUser.displayName
                .split(" ")
                .map((word) => word.charAt(0))
                .join("")
                .toLowerCase(),
              accessToken: auth.currentUser.accessToken,
              providers: auth.currentUser.providerData,
            });
          } else {
            // Error
          }
        });
        this.snapshots.push(snapshot);
      } else {
        if (this.$router.currentRoute.name !== "login") {
          this.$router.replace({
            name: "login",
          });
        }
      }
    },
    async getNotifications() {
      this.response.notifications = [];
      const notificationsRef = collection(firestore, "notifications");
      const relatedUser = or(
        where("assignedTo.uid", "==", this.getUser.uid),
        where("createdBy.uid", "==", this.getUser.uid),
      );

      const q = query(notificationsRef, relatedUser);
      let snapshot = onSnapshot(q, (response) => {
        if (!response.empty) {
          this.response.notifications = response.docs
            .map((notification) => {
              const data = notification.data();
              const createdByName = data.createdBy?.name;
              const assignedToName = data.assignedTo?.name;
              return {
                id: notification.id,
                viewed: data.viewed,
                createdBy: createdByName
                  ? `${createdByName.first ?? ""} ${
                      createdByName.last ?? ""
                    }`.trim()
                  : "Unknown",
                assignedTo: assignedToName
                  ? `${assignedToName.first ?? ""} ${
                      assignedToName.last ?? ""
                    }`.trim()
                  : "Unknown",
                created: moment(data.created?.seconds * 1000).toDate(),
                updated: moment(data.updated?.seconds * 1000).toDate(),
                relatedTo: data.relatedTo,
                notes: data.notes,
                createdByUID: data.createdBy?.uid,
                assignedToUID: data.assignedTo?.uid,
                name: data.relatedTo?.name,
                type: data.relatedTo?.type,
                icon: data.icon ?? null,
              };
            })
            .sort((a, b) => (a.created > b.created ? -1 : 1));
        } else {
          this.response.notifications = [];
        }
      });
      this.snapshots.push(snapshot);
    },
    async getAlertRecords() {
      this.response.alerts.conditions = [];
      const alertsRef = collection(firestore, "watchers");
      const relatedUser = where("assignedTo.uid", "==", this.getUser.uid);

      const q = query(alertsRef, relatedUser);
      let snapshot = onSnapshot(q, (response) => {
        if (!response.empty) {
          this.response.alerts.conditions = response.docs
            .map((alert) => {
              return {
                id: alert.id,
                ...alert.data(),
                created: moment(alert.data().created?.seconds * 1000).toDate(),
                completed: alert.data().completed
                  ? moment(alert.data().completed?.seconds * 1000).toDate()
                  : null,
              };
            })
            .sort((a, b) => (a.created > b.created ? 1 : -1));
        } else {
          this.response.alerts.conditions = [];
        }
        this.getRelatedAlertRecords();
      });
      this.snapshots.push(snapshot);
    },
    async getRelatedAlertRecords() {
      this.response.alerts.records = [];

      this.relatedAlertRecords.forEach(async (record) => {
        const recordDoc = doc(firestore, record.resource, record.id);
        let snapshot = onSnapshot(recordDoc, async (response) => {
          if (
            this.response.alerts.records.find((item) => item.id === record.id)
          ) {
            this.response.alerts.records.find(
              (item) => item.id === record.id,
            ).data = response.data();
          } else {
            let recordType = {
              icon: "",
              type: record.resource,
            };

            switch (record.resource) {
              case "tasks":
                recordType["icon"] = this.$vuetify.icons.values.task;
                break;
              case "salesOrders":
                recordType["icon"] = this.$vuetify.icons.values.order;
                break;
            }

            this.response.alerts.records.push({
              id: response.id,
              data: response.data(),
              type: recordType,
              alerts: this.response.alerts.conditions.filter(
                (alert) => alert.relatedTo.id === response.id,
              ),
            });
          }
        });
        this.snapshots.push(snapshot);
      });
    },
    async speechRecoginition() {
      const speech = new (window.speechRecoginition ||
        window.webkitSpeechRecognition)();

      if (this.microphone) {
        this.microphone = false;
        speech.stop();
      } else {
        if (
          "SpeechRecognition" in window ||
          "webkitSpeechRecognition" in window
        ) {
          this.microphone = true;
          speech.continuous = true;
          speech.lang = "en-US";
          speech.start();
          speech.onstart = () => {
            console.log("Voice recoginition started");
          };

          speech.onresult = async (event) => {
            this.microphone = true;
            const allCommands = event.results.length - 1;
            const latestCommand = event.results[allCommands][0].transcript
              .trim()
              .toLowerCase();

            if (latestCommand.includes("stop listening")) {
              this.microphone = false;
              speech.stop();
            } else if (
              latestCommand.includes("log out") ||
              latestCommand.includes("logout")
            ) {
              speech.stop();
              await this.logout();
              this.$router.go();
            } else if (
              latestCommand.includes("show navigation") ||
              latestCommand.includes("open navigation")
            ) {
              this.drawer = true;
            } else if (
              latestCommand.includes("hide navigation") ||
              latestCommand.includes("close navigation")
            ) {
              this.drawer = false;
            } else if (
              latestCommand.includes("show notifications") ||
              latestCommand.includes("open notifications")
            ) {
              this.traverse("notifications-view");
            } else if (latestCommand.includes("search accounts")) {
              event.preventDefault();
              clearTimeout(this.shortCutHoldTimers.tutorial);
              this.shortCutHoldTimers.tutorial = null;
              this.menu = true;
              this.$refs.algolia.$el.focus();
              this.$refs.algolia.$refs.input.select();
            } else if (this.$refs.algolia.hasFocused) {
              console.log(this.$refs.algolia, event.results, latestCommand);
              this.accountSearch = latestCommand;
              this.handleAlgoliaSearch();
            } else if (
              latestCommand.includes("go to home") ||
              latestCommand.includes("go home")
            ) {
              this.$router.push({
                name: "home",
              });
            } else if (latestCommand.includes("go back")) {
              this.$router.go(-1);
            }
            // Routes
            else if (
              latestCommand.includes("go to brands overview") ||
              latestCommand.includes("go to the brands overview")
            ) {
              this.$router.push({
                name: "brands-overview",
              });
            } else if (
              latestCommand.includes("go to locations overview") ||
              latestCommand.includes("go to the locations overview")
            ) {
              this.$router.push({
                name: "locations-overview",
              });
            } else if (
              latestCommand.includes("go to vendor orders overview") ||
              latestCommand.includes("go to the vendor orders overview")
            ) {
              this.$router.push({
                name: "vendorOrders-overview",
              });
            } else if (
              latestCommand.includes("go to raw material orders overview") ||
              latestCommand.includes("go to the raw material orders overview")
            ) {
              this.$router.push({
                name: "rawMaterialOrders-overview",
              });
            } else if (
              latestCommand.includes("go to accounts overview") ||
              latestCommand.includes("go to the accounts overview")
            ) {
              this.$router.push({
                name: "accounts-overview",
              });
            } else if (
              latestCommand.includes("go to pending sales orders") ||
              latestCommand.includes("go to the pending sales orders")
            ) {
              this.$router.push({
                name: "saleOrders-overview",
              });
            } else if (
              latestCommand.includes("go to delivered sales orders") ||
              latestCommand.includes("go to the delivered sales orders")
            ) {
              this.$router.push({
                name: "deliveredOrders-overview",
              });
            } else if (
              latestCommand.includes("go to store events overview") ||
              latestCommand.includes("go to the store events overview")
            ) {
              this.$router.push({
                name: "storeEvents-overview",
              });
            } else if (
              latestCommand.includes("go to trainings overview") ||
              latestCommand.includes("go to the trainings overview") ||
              latestCommand.includes("go to training overview")
            ) {
              this.$router.push({
                name: "budtenderTrainings-overview",
              });
            }
            // Reports
            else if (
              latestCommand.includes("go to trace reports") ||
              latestCommand.includes("go to the trace reports")
            ) {
              this.$router.push({
                name: "reports-overview",
              });
            } else if (
              latestCommand.includes("go to the vendor orders trace report")
            ) {
              this.$router.push({
                name: "sourcing-orders-report",
              });
            } else if (
              latestCommand.includes("go to the inventory trace report") ||
              latestCommand.includes("go to the inventory report") ||
              latestCommand.includes("go to inventory report")
            ) {
              this.$router.push({
                name: "manufacturing-inventory-report",
              });
            } else if (
              latestCommand.includes("go to the sales orders trace report") ||
              latestCommand.includes("go to the sales orders report") ||
              latestCommand.includes("go to sales orders report")
            ) {
              this.$router.push({
                name: "sales-orders-report",
              });
            } else if (
              latestCommand.includes("go to the ar trace report") ||
              latestCommand.includes(
                "go to the accounts receivable trace report",
              ) ||
              latestCommand.includes("go to accounts receivable report") ||
              latestCommand.includes("go to accounts receivable")
            ) {
              this.$router.push({
                name: "sales-ar-report",
              });
            } else if (
              latestCommand.includes("go to the store events trace report")
            ) {
              this.$router.push({
                name: "support-storeEvents-report",
              });
            } else {
              console.log("not recognized");
            }
          };
        } else {
          console.log("Speech recognition not supported");
        }
      }
    },
    // POST
    async createNotification() {
      this.loading.submit = true;
      const batch = writeBatch(firestore);
      const devicesRef = collection(firestore, "devices");
      const slackActive = getValue(remoteConfig, "slack").asBoolean();

      let slackAPI = null;
      if (slackActive) {
        const integrationsRef = collection(firestore, "integrations"),
          related = where("platform", "==", "slack");
        const q = query(integrationsRef, related, limit(1));
        const response = await getDocs(q);
        if (!response.empty) {
          slackAPI = response.docs[0].data();
        }
      }

      for (
        let index = 0;
        index < this.getNotificationRecord.assignedTo.length;
        index++
      ) {
        const userID = this.getNotificationRecord.assignedTo[index];
        const user = this.getNotificationUsers.find(
          (option) => option.value === userID,
        );
        let url = `${window.location.origin}/${this.getNotificationRecord.related.resource}/${this.getNotificationRecord.related.id}`;

        let deviceIDs = [];
        const userDevicesQ = query(devicesRef, where("userID", "==", userID));
        const userDevices = await getDocs(userDevicesQ);
        if (!userDevices.empty) {
          deviceIDs = userDevices.docs.map((device) => device.id);
        }

        batch.set(
          doc(collection(firestore, "notifications")),
          {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            assignedTo: {
              name: user.name,
              uid: user.value,
            },
            createdBy: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            lastUpdated: {
              name: this.getUser.name,
              uid: this.getUser.uid,
            },
            notes: this.getNotificationRecord.notes,
            relatedTo: this.getNotificationRecord.related,
            viewed: false,
          },
          { merge: true },
        );

        if (slackAPI) {
          await this.sendSlackMessage({
            apiKey: slackAPI.apiKey,
            email: user.email,
            text: `:bell: ${this.$options.filters.capitalize(
              this.getUser?.name?.first ?? "",
            )} ${this.$options.filters.capitalize(
              this.getUser?.name?.last ?? "",
            )} has sent you a new notification\nRecord: ${this.$options.filters.capitalize(
              this.getNotificationRecord.related?.name ?? "",
            )} | ${this.$options.filters.capitalize(
              this.getNotificationRecord.related?.type ?? "",
            )}\nNotes: "${this.getNotificationRecord.notes ?? ""}"`,
            postAt: moment().unix(),
            url: "https://slack.com/api/chat.postMessage",
          });
        }
        // if (deviceIDs.length > 0) {

        //   await this.sendPush({
        //     url,
        //     title: `${this.getNotificationRecord.related.name} - ${this.$options.filters.capitalize(this.getNotificationRecord.related.type)}`,
        //     body: this.getNotificationRecord.notes,
        //     devices: deviceIDs,
        //   });
        // }
      }

      await batch.commit();
      this._notification({ dialog: false });
      this.loading.submit = false;
    },
    async sendSlackMessage(payload) {
      const slackMessage = httpsCallable(functions, "slackMessage");
      await slackMessage(payload);
    },
    async sendPush(payload) {
      const pushNotification = httpsCallable(functions, "sendPushNotification");
      await pushNotification({
        title: payload.title,
        body: payload.body,
        url: payload.url,
        devices: payload.devices,
      });
    },
    async createEmail() {
      this.loading.submit = true;
      const batch = writeBatch(firestore);

      const to = this.getEmailRecord.to.map((record) => {
        if (typeof record === "string") {
          return record;
        }
        return record.email;
      });

      const sendEmail = httpsCallable(functions, "sendEmail");
      let result = await sendEmail({
        uid: this.getUser.uid,
        name: this.getUser.name,
        email: this.getUser.email,
        subject: this.getEmailRecord.subject,
        body: this.getEmailRecord.body,
        to,
        cc: this.getEmailRecord.cc,
        bcc: this.getEmailRecord.bcc,
        attachments: this.getEmailRecord.attachments,
      });

      await batch.commit();

      // Task
      this._email({ dialog: false });
      this.loading.submit = false;
    },
    async deleteAlert(alert) {
      const batch = writeBatch(firestore);

      const alertDoc = doc(firestore, "watchers", alert.id);
      batch.delete(alertDoc);

      await batch.commit();
    },
    /* Main */
    async setup() {
      this.loading.overall = true;
      if (auth.currentUser) {
        await this.getUserMetadata();
        await this.getAlertRecords();
        await this.getNotifications();
        const allViewValues = (this.$store.getters.getPhaseViews || []).map(
          (v) => v.value,
        );
        await this.$store.dispatch("fetchRolePermissions", { allViewValues });
      }
      this.loading.overall = false;
    },
    async traverse(action, params) {
      let link,
        relatedToResource = "";
      switch (action) {
        case "helpdesk-view":
          this._rightDrawer({
            active: true,
            title: "Help",
            type: "helpdesk",
            data: {},
          });
          break;
        case "profile-view":
          this.$router.push({
            name: "profile-view",
            params: {
              id: this.getUser.uid,
            },
          });
          break;
        case "account-view":
          this.$refs.menu.save();

          if (this.$router.currentRoute.name === "accounts-view") {
            this.$router.push({
              name: "accounts-view",
              params: {
                id: params.objectID,
              },
            });
            this.$router.go();
          } else {
            this.$router.push({
              name: "accounts-view",
              params: {
                id: params.objectID,
              },
            });
          }
          break;
        case "alert-view":
          this._rightDrawer({
            active: true,
            title: "Alerts",
            type: "alerts",
            data: {},
          });
          break;
        case "alertRecord-view":
          switch (params.relatedTo.resource) {
            case "tasks":
              relatedToResource = "task";
              break;
            case "salesOrders":
              relatedToResource = "salesOrder";
              break;
          }

          if (this.$router.currentRoute.name === `${relatedToResource}-view`) {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }

            this.$router.push({
              name: `${relatedToResource}-view`,
              params: {
                ...routerParams,
              },
            });
            this.$router.go();
          } else {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }
            this.$router.push({
              name: `${relatedToResource}-view`,
              params: {
                ...routerParams,
              },
            });
          }
          break;
        case "alert-delete":
          this.deleteAlert(params);
          break;
        case "phoneLog-view":
          this._rightDrawer({
            active: true,
            title: "Call History",
            type: "phoneLog",
            data: {},
          });
          break;
        case "notifications-view":
          this._rightDrawer({
            active: true,
            title: "Notifications",
            type: "notifications",
            data: {},
          });
          break;
        case "notification-view":
          this.$refs.notificationMenu.save();
          if (!params.viewed && this.activeTab.notifications !== 2) {
            await updateDoc(
              doc(firestore, "notifications", params.id),
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: this.getUser.name,
                  uid: this.getUser.uid,
                },
                viewed: true,
              },
              { merge: true },
            );
          }
          if (
            this.$router.currentRoute.name ===
            `${params.relatedTo.resource}-view`
          ) {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }

            this.$router.push({
              name: `${params.relatedTo.resource}-view`,
              params: {
                ...routerParams,
              },
            });
            this.$router.go();
          } else {
            let routerParams = {
              id: params.relatedTo.id,
            };

            if (params.relatedTo.locationID) {
              routerParams.locationID = params.relatedTo.locationID;
            }
            this.$router.push({
              name: `${params.relatedTo.resource}-view`,
              params: {
                ...routerParams,
              },
            });
          }
          break;
        case "notification-submit":
          this.createNotification();
          break;
        case "notification-cancel":
          this._notification({ dialog: false });
          break;
        case "email-submit":
          this.createEmail();
          break;
        case "email-cancel":
          this._email({ dialog: false });
          break;
      }
    },
    async logout() {
      this.snapshots.forEach((snapshot) => {
        snapshot();
      });
      await signOut(auth);
      this.$router.replace({
        name: "login",
      });
      this._user(null);
    },
    setTemplate() {
      if (!this.getEmailRecord.template) {
        this.getEmailRecord.subject = "";
        this.getEmailRecord.body = "";
        return;
      }

      const selected = this.getEmailRecord.templates.find(
        (option) => option.value === this.getEmailRecord.template,
      );
      let body = selected.body;

      switch (this.getEmailRecord.phase) {
        case "sales":
          body = body.replaceAll(
            "(Storefront Name)",
            this.$options.filters.capitalize(this.getEmailRecord.related.name),
          );
          body = body.replaceAll(
            "(Account Executive)",
            this.$options.filters.capitalize(
              `${
                this.getEmailRecord.related.accountExecutive.name.split(" ")[0]
              }`,
            ),
          );
          body = body.replaceAll(
            "(Account Executive Email)",
            this.getEmailRecord.related.accountExecutive.email,
          );
          break;
      }

      this.getEmailRecord.subject = `${selected.subject} | ${moment().format(
        "MM/DD/YY",
      )}`;
      this.getEmailRecord.body = body;
    },
    successSnackbarActivate(payload) {
      this.successSnackbar.active = payload.active;
      this.successSnackbar.msg = payload.msg;
      setTimeout(() => {
        this.successSnackbar.active = false;
        this.successSnackbar.msg = "";
      }, 2500);
    },
    /* Misc */
    uploadFile(section) {
      var input = document.createElement("input");
      input.type = "file";
      // input.accept = "image/*";

      input.onchange = (e) => {
        // getting a hold of the file reference
        var file = e.target.files[0];
        let fileName = file.name;

        // setting up the reader
        var reader = new FileReader();
        reader.readAsDataURL(file); // this is reading as data url

        // here we tell the reader what to do when it's done reading...
        reader.onload = (readerEvent) => {
          var content = readerEvent.target.result; // this is the content!
          let result = {
            filename: fileName,
            path: content,
          };
          this.getEmailRecord.attachments.push(result);
        };
      };
      input.click();
    },
    removeAttachment(attachment) {
      const index = this.getEmailRecord.attachments.indexOf(attachment);
      if (index > -1) {
        // only splice array when item is found
        this.getEmailRecord.attachments.splice(index, 1); // 2nd parameter means remove one item only
      }
    },
    cleanInput(field) {
      if (
        this.getEmailRecord[field].filter((record) =>
          typeof record === "string"
            ? !this.isValidEmail(record)
            : !this.isValidEmail(record.email),
        ).length > 0
      ) {
        this.errorSnackbar = {
          active: true,
          msg: `Invalid email address entered!`,
        };
      }
      this.getEmailRecord[field] = this.getEmailRecord[field].filter((record) =>
        typeof record === "string"
          ? this.isValidEmail(record)
          : this.isValidEmail(record.email),
      );
    },
    handleAlgoliaSearch(refine, value) {
      if (value?.length > 0) {
        this.menu = true;
        this.loading.algolia = true;
        this.refineSearch(refine, value);
      } else {
        this.menu = false;
        this.loading.algolia = false;
        refine(""); // Clear the search results
        this.$nextTick(() => {
          if (this.$refs.algolia) {
            this.$refs.algolia.$refs.input.value = "";
          }
        });
      }
    },
    handleClearSearch(refine) {
      this.menu = false;
      this.loading.algolia = false;
      refine(""); // Clear the search results
      this.$nextTick(() => {
        if (this.$refs.algolia) {
          this.$refs.algolia.$refs.input.value = "";
        }
      });
    },
    refineSearch: _.debounce(async function (refine, currentRefinement) {
      await refine(currentRefinement);
      this.loading.algolia = false;
    }, 300),
    async handleOrderSubmit() {
      this.loading.orderSubmit = true;
      await this.$refs.OrderCart.submit();
      this.loading.orderSubmit = false;
    },
    async handleInvoiceDownload() {
      this.loading.invoice = true;
      await this.$refs.OrderCart.downloadInvoice();
      this.loading.invoice = false;
    },
    getDateAndTime() {
      this.dateAndTime = moment().toDate();
    },
    handleKeyDown(event) {
      if (!event?.key) return;
      let isMetaKey =
        event.metaKey ||
        event.ctrlKey ||
        event?.key?.toLowerCase() === this.metaKey.key;

      let isAltKey = event.altKey || event.key.toLowerCase() === "alt";
      let isShiftKey = event.shiftKey || event.key.toLowerCase() === "shift";
      // if (isMetaKey) {
      //   if (!this.shortCutHoldTimers.tutorial) {
      //     this.shortCutHoldTimers.tutorial = setTimeout(() => {
      //       this.dialog.keyboardShortcuts = true;
      //     }, 1500);
      //   }
      // }
      if (event.key === "d" && isMetaKey && isShiftKey) {
        event.preventDefault();
        clearTimeout(this.shortCutHoldTimers.tutorial);
        this.shortCutHoldTimers.tutorial = null;
        this.toggleTheme();
      } else if ((event.key === "/" || event.code === "Slash") && isMetaKey) {
        event.preventDefault();
        clearTimeout(this.shortCutHoldTimers.tutorial);
        this.shortCutHoldTimers.tutorial = null;
        this.drawer = true;

        this.$nextTick(() => {
          if (this.$refs.mainNav && this.miniNav) {
            this.miniNav = false;
          }
        });
        this.$nextTick(() => {
          setTimeout(() => {
            this.$refs.viewSearch.$el.focus();
            this.$refs.viewSearch.$refs.input.select();
          }, 300);
        });
      } else if (event.key === "k" && isMetaKey) {
        event.preventDefault();
        clearTimeout(this.shortCutHoldTimers.tutorial);
        this.shortCutHoldTimers.tutorial = null;
        this.$refs.appBar.handleSearchClick();
      }
    },
    handleKeyUp(event) {
      let isMetaKey =
        event.metaKey ||
        event.ctrlKey ||
        event.key?.toLowerCase() === this.metaKey.key;
      if (isMetaKey) {
        clearTimeout(this.shortCutHoldTimers.tutorial);
        this.shortCutHoldTimers.tutorial = null;
        this.dialog.keyboardShortcuts = false;
      }
    },
    handleWindowBlur() {
      clearTimeout(this.shortCutHoldTimers.tutorial);
      this.shortCutHoldTimers.tutorial = null;
      this.dialog.keyboardShortcuts = false;
    },
    filteredPhaseViews(phase) {
      if (this.payload.search.views?.length > 1) {
        let normalizedSearchQuery = this.payload.search.views.toLowerCase();
        let phaseMatch = phase.text
          .toLowerCase()
          .includes(normalizedSearchQuery);
        if (phaseMatch) {
          return phase.views;
        }
        return phase.views.filter((phaseView) => {
          let phaseViewMatch = phaseView.text
            .toLowerCase()
            .includes(normalizedSearchQuery);
          return phaseViewMatch;
        });
      }
      return phase.views;
    },
    handleViewSearch() {
      if (this.payload.search.views?.length > 1) {
        for (let i = 0; i < this.filteredViews.length; i++) {
          this.navGroups[i] = true;
        }
        // Expand reports if search matches
        if (this.filteredReportCategories.length > 0) {
          this.reportsNavGroup = true;
          this.filteredReportCategories.forEach((_, index) => {
            this.reportsCategoryGroups[index] = true;
          });
        }
      }
      if (!this.payload.search.views?.length) {
        for (let i = 0; i < this.navGroups.length; i++) {
          this.navGroups[i] = false;
        }
        this.reportsNavGroup = false;
        this.reportsCategoryGroups = {};
      }
    },
    getReportCategoryColor(type) {
      const typeConfig = {
        admin: "#000",
        sourcing: "#F57C00",
        manufacturing: "#9C27B0",
        sales: "#4CAF50",
        "retail success": "#607D8B",
        integrations: "#26C6DA",
      };
      return typeConfig[type] || "#757575";
    },
    getReportCategoryIcon(type) {
      const iconConfig = {
        admin: "mdi-shield-account",
        sourcing: "mdi-source-fork",
        manufacturing: "mdi-hammer-wrench",
        sales: "mdi-cash-multiple",
        "retail success": "mdi-face-agent",
        integrations: "mdi-dip-switch",
      };
      return iconConfig[type] || "mdi-folder";
    },
    blurViewSearch() {
      if (!this.payload.search.views?.length) {
        this.expandNavOnHover = this.$vuetify.breakpoint.smAndUp;
      }
    },
    matchViewName(current) {
      let viewName = this.$options.filters.capitalize(current);
      if (
        !this.payload.search.views?.length ||
        this.payload.search.views.length < 2
      )
        return this.$options.filters.capitalize(viewName);
      let pattern = new RegExp(this.payload.search.views, "ig");
      let found = viewName.search(pattern) !== -1;
      let result = viewName;
      if (found) {
        let matches = viewName.match(pattern);
        while (matches.length) {
          viewName = matches.shift();
          let caseSensitivePattern = new RegExp(viewName, "g");
          result = result.replace(
            caseSensitivePattern,
            `<span class='highlighted-text rounded'>` + viewName + `</span>`,
          );
        }
      }
      return !found ? this.$options.filters.capitalize(result) : result;
    },
    async handleKeyboardNavigation(view, event) {
      let isArrowKey = ["ArrowDown", "ArrowUp"].includes(event.key);
      if (isArrowKey) {
        let defaultViews = [
          { text: "Search", value: "search" },
          { text: "Home", value: "home", route: "/" },
        ];
        let phaseViews = this.filteredViews.flatMap((phase) =>
          this.filteredPhaseViews(phase),
        );
        // Add reports overview and individual reports
        let reportViews = [];
        if (this.filteredReportCategories.length > 0) {
          reportViews.push({
            text: "Trace Reports",
            value: "reports-overview",
            route: "/reporting/overview",
          });
          reportViews = reportViews.concat(
            this.filteredReportCategories.flatMap(
              (category) => category.reports,
            ),
          );
        }
        let allViews = defaultViews
          .concat(phaseViews)
          .concat(this.filteredRoutes)
          .concat(reportViews);
        let index = allViews.findIndex((item) => item.value === view.value);
        if (event.key === "ArrowDown") {
          event.preventDefault();
          this.focusedIndex = (index + 1) % allViews.length; // Move to next item
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          this.focusedIndex = (index - 1 + allViews.length) % allViews.length; // Move to previous item
        }
        let correctViewLog = allViews[this.focusedIndex] ?? null;
        let correctView = correctViewLog?.route ?? null;
        try {
          let navGroupIndex = this.filteredViews.findIndex(
            (phase) => phase.phase === correctViewLog.phase,
          );
          if (this.payload.search.views?.length) {
            this.navGroups[navGroupIndex] = true;
            // Expand reports if navigating to a report
            if (reportViews.some((r) => r.value === correctViewLog?.value)) {
              this.reportsNavGroup = true;
            }
          } else {
            this.navGroups = {
              [navGroupIndex]: true,
            };
            // Expand reports if navigating to a report
            if (reportViews.some((r) => r.value === correctViewLog?.value)) {
              this.reportsNavGroup = true;
            }
          }

          this.$nextTick(() => {
            let refs = this.$refs.navItem.concat([this.$refs["navItem-home"]]);
            let navItem = refs.find((item) => item.to === correctView);
            if (navItem) {
              navItem.$el.focus();
            } else if (this.focusedIndex === 0) {
              this.$refs.viewSearch?.$el?.focus();
              this.$refs.viewSearch?.$refs?.input?.select();
            } else {
              this.$refs["navItem-home"]?.$el?.focus();
            }
          });
        } catch (error) {
          console.warn(error.message);
        }
      }
    },
    async trackReportView(reportTo) {
      // Track report view when navigating from sidebar
      if (this.getUser?.uid && reportTo) {
        try {
          await ReportTrackingService.trackReportView(
            reportTo,
            this.getUser.uid,
          );
        } catch (error) {
          console.error("Error tracking report view from navigation:", error);
          // Don't block navigation if tracking fails
        }
      }
    },
    getViewIndex(view) {
      let defaultViews = [
        { text: "Search", value: "search" },
        { text: "Home", value: "home" },
      ];
      let phaseViews = this.filteredViews.flatMap((phase) =>
        this.filteredPhaseViews(phase),
      );
      // Add reports overview and individual reports
      let reportViews = [];
      if (this.filteredReportCategories.length > 0) {
        reportViews.push({
          text: "Trace Reports",
          value: "reports-overview",
          route: "/reporting/overview",
        });
        reportViews = reportViews.concat(
          this.filteredReportCategories.flatMap((category) => category.reports),
        );
      }
      let allViews = defaultViews
        .concat(phaseViews)
        .concat(this.filteredRoutes)
        .concat(reportViews);
      let index = allViews.findIndex((item) => item.value === view.value);
      return index;
    },
    handleSearch() {
      this.drawer = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.viewSearch.$el.focus();
          this.$refs.viewSearch.$refs.input.select();
        }, 200);
      });
    },
    openMenu() {
      setTimeout(() => {
        this.menu = true;
      }, 50);
    },
    openSupportTicket() {
      window.open(
        "https://mytrace-support-tickets.web.app/",
        "_blank",
        "noopener,noreferrer",
      );
    },
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem(
        "theme",
        this.$vuetify.theme.dark ? "dark" : "light",
      );
      themeChannel.postMessage({
        theme: this.$vuetify.theme.dark ? "dark" : "light",
      });
      const metaThemeColor = document.querySelector("meta[name=theme-color]");
      metaThemeColor.setAttribute(
        "content",
        this.$vuetify.theme.currentTheme.background,
      );
    },
    algoliaMenuHandler(origHandler) {
      // TODO: fix this shit. menu closes when clicking text field if menu is already open
      // console.log(origHandler);
      if (this.menu) {
        let tempHandler = JSON.parse(JSON.stringify(origHandler));
        tempHandler["click"] = () => {
          console.log("skipped event");
        };
        return {};
      } else return origHandler;
    },
  },
  computed: {
    ...mapGetters([
      "getAppName",
      "getUser",
      "getPhases",
      "getPhaseViews",
      "getDepartments",
      "getUserRoles",
      "getActivityDialog",
      "getNotificationDialog",
      "getNotificationRecord",
      "getNotificationUsers",
      "getNotificationUsersLoading",
      "getEmailDialog",
      "getEmailRecord",
      "getEmailUsers",
      "getEmailUsersLoading",
      "getRightDrawer",
      "getAccountStatus",
      "getPaymentTerms",
      "getActionListDialog",
      // Alerts
      "getAlertDialog",
      "getAlerts",
      "getTaskStatus",
      "getOrderStatus",
      "getReports",
    ]),
    filteredViews() {
      let views = this.views.filter((item) => item.value !== "reports");
      if (this.payload.search.views?.length > 1) {
        let normalizedSearchQuery = this.payload.search.views.toLowerCase();
        return views.filter((phase) => {
          let phaseMatch = phase.text
            .toLowerCase()
            .includes(normalizedSearchQuery);
          let phaseViewsMatch = phase.views.some((view) =>
            view.text.toLowerCase().includes(normalizedSearchQuery),
          );
          return phaseMatch || phaseViewsMatch;
        });
      }
      return views;
    },
    filteredRoutes() {
      if (this.payload.search.views?.length > 0) {
        let normalizedSearchQuery = this.payload.search.views.toLowerCase();
        return this.phaseRoutes.filter((view) => {
          let normalizedText = view.text.toLowerCase();
          return normalizedText.includes(normalizedSearchQuery);
        });
      }
      return this.phaseRoutes;
    },
    isAuthenticated() {
      return this.getUser ? true : false;
    },
    views() {
      let views = [];

      if (this.isAuthenticated) {
        if (this.getUser.accountType >= 4) {
          views = this.getPhases
            .map((phase) => {
              const views = this.getPhaseViews.filter(
                (view) => view.phase === phase.phase,
              );
              return {
                ...phase,
                views,
              };
            })
            .sort((a, b) => (a.sort > b.sort ? 1 : -1));
        } else {
          if (this.getUser.views.length > 0) {
            this.getPhases.forEach((phase) => {
              const phaseViews = this.getPhaseViews
                .filter((view) => view.phase === phase.phase)
                .sort((a, b) => (a.sort > b.sort ? 1 : -1));
              const userViews = phaseViews.filter((view) =>
                this.getUser.views.includes(view.value),
              );
              if (userViews.length > 0) {
                views.push({
                  ...phase,
                  views: userViews,
                });
              }
            });
            if (views.length > 0) {
              views = views.sort((a, b) => (a.sort > b.sort ? 1 : -1));
            }
            views = views;
          }
        }
      }

      return views;
    },
    rightDrawer: {
      get: function () {
        return this.getRightDrawer;
      },
      set: function (value) {
        this._rightDrawer({
          ...this.getRightDrawer,
          active: value,
        });
      },
    },
    isUserLoggedIn() {
      return auth.currentUser ? true : false;
    },
    currentUser() {
      return this.isUserLoggedIn ? auth.currentUser : false;
    },
    phaseRoutes() {
      return this.getPhases.filter(
        (phase) =>
          phase.route &&
          (this.getUser?.views.includes(phase.routeValue) ||
            this.getUser?.accountType >= 4),
      );
    },
    reportCategories() {
      if (!this.getReports || this.getReports.length === 0) return [];

      const categories = {};
      this.getReports.forEach((report) => {
        if (!categories[report.type]) {
          categories[report.type] = [];
        }
        // Filter by user permissions: empty reports = none visible; non-empty = only those listed
        if (this.getUser?.accountType >= 4) {
          categories[report.type].push(report);
        } else if (
          Array.isArray(this.getUser?.reports) &&
          this.getUser.reports.length > 0 &&
          this.getUser.reports.includes(report.to)
        ) {
          categories[report.type].push(report);
        }
      });

      return Object.keys(categories).map((type) => ({
        type,
        reports: categories[type],
      }));
    },
    filteredReportCategories() {
      if (
        !this.payload.search.views?.length ||
        this.payload.search.views.length < 2
      ) {
        return this.reportCategories;
      }

      const query = this.payload.search.views.toLowerCase();
      return this.reportCategories
        .map((category) => {
          const filteredReports = category.reports.filter((report) => {
            const textMatch = report.text?.toLowerCase().includes(query);
            const descMatch = report.desc?.toLowerCase().includes(query);
            const typeMatch = category.type.toLowerCase().includes(query);
            return textMatch || descMatch || typeMatch;
          });
          return {
            ...category,
            reports: filteredReports,
          };
        })
        .filter((category) => category.reports.length > 0);
    },
    totalReportsCount() {
      return this.filteredReportCategories.reduce(
        (total, category) => total + category.reports.length,
        0,
      );
    },
    pushNotificationsEnabled() {
      return this.response.devices.includes(this.fcm) ? true : false;
    },
    // Notifications
    notificationTabColor() {
      switch (this.activeTab.notifications) {
        case 0:
          return this.$vuetify.theme.currentTheme.review;
          break;
        case 1:
          return this.$vuetify.theme.currentTheme.primary;
          break;
        case 2:
          return this.$vuetify.theme.currentTheme.ledger;
          break;
      }
    },
    unreadNotifications() {
      if (!this.getUser?.uid?.length) return [];
      return this.response.notifications.filter(
        (notification) =>
          !notification.viewed &&
          notification.assignedToUID === this.getUser.uid,
      );
    },
    allNotifications() {
      if (!this.getUser?.uid?.length) return [];
      return this.response.notifications.filter(
        (item) => item.assignedToUID === this.getUser.uid,
      );
    },
    sentNotifications() {
      if (!this.getUser?.uid?.length) return [];
      return this.response.notifications.filter(
        (item) => item.createdByUID === this.getUser.uid,
      );
    },
    notifications() {
      switch (this.activeTab.notifications) {
        case 0:
          return this.unreadNotifications;
          break;
        case 1:
          return this.allNotifications;
          break;
        case 2:
          return this.sentNotifications;
          break;
        default:
          return [];
          break;
      }
    },
    notificationAssigneeSet() {
      return this.getNotificationRecord.assignedTo.length > 0 ? true : false;
    },
    notificationNotesSet() {
      return this.getNotificationRecord.notes.length > 0 ? true : false;
    },
    invalidNotificationSubmission() {
      if (this.getNotificationRecord) {
        return this.notificationAssigneeSet && this.notificationNotesSet
          ? false
          : true;
      } else {
        return false;
      }
    },
    // Emails
    emailToSet() {
      return this.getEmailRecord.to.length > 0 ? true : false;
    },
    emailSubjectSet() {
      return this.getEmailRecord.subject.length > 0 ? true : false;
    },
    emailBodySet() {
      return this.getEmailRecord.body.length > 0 ? true : false;
    },
    invalidEmailSubmission() {
      if (this.getEmailRecord) {
        return this.emailToSet &&
          this.emailSubjectSet &&
          this.emailBodySet &&
          !this.getEmailUsersLoading
          ? false
          : true;
      } else {
        return false;
      }
    },
    cartSubmitLoading() {
      return this.$refs.OrderCart?.loading?.submit || this.loading.orderSubmit;
    },
    disableSubmission() {
      return this.$refs.OrderCart?.invalidSubmission;
    },
    userAccessSubtitle() {
      if (this.getRightDrawer?.type !== "userAccess") return null;
      const item = this.getRightDrawer?.data?.item;
      if (!item) return null;
      const role = item.role || "";
      const accountType = item.accountType || "";
      if (!role && !accountType) return null;
      return { role, accountType };
    },
    rightDrawerIcon() {
      if (!this.getRightDrawer.active) return null;
      switch (this.getRightDrawer.type) {
        case "task":
          return this.$vuetify.icons.values.task;
          break;
        case "salesOrder":
          return this.$vuetify.icons.values.order;
          break;
        case "account":
          return this.$vuetify.icons.values.account;
          break;
        case "supportTicket":
          return this.$vuetify.icons.values.supportTicket;
          break;
        case "category":
          return this.$vuetify.icons.values.productCategory;
          break;
        case "product":
          return this.$vuetify.icons.values.products;
          break;
        case "vendorOrder":
          return this.$vuetify.icons.values.vendorOrder;
          break;
        case "rawMaterialOrder":
        case "customOrder":
          return this.$vuetify.icons.values.order;
          break;
        case "budtenderTraining":
          return this.$vuetify.icons.values.budtenderTraining;
          break;
        case "notifications":
          return this.$vuetify.icons.values.notification;
          break;
        case "phoneLog":
          return this.$vuetify.icons.values.phoneNumber;
          break;
        case "alerts":
          return this.$vuetify.icons.values.alerts;
          break;
        case "helpdesk":
          return "mdi-help-circle";
          break;
        case "cart":
          return this.rightDrawer.data.cartWindow === 0
            ? "mdi-cart"
            : "mdi-cart-arrow-right";
          break;
        case "activityReplies":
          return "mdi-comment-multiple";
          break;
        case "userAccess":
          return "mdi-shield-account";
          break;
      }
    },
    topPadding() {
      if (!this.getUser) return 0;
      return this.isMobileDevice ? "56px" : "64px";
    },
    // Alerts
    relatedAlertRecords() {
      let docIDSet = new Set();
      this.response.alerts.conditions.forEach((alert) =>
        docIDSet.add(alert.relatedTo.id),
      );

      return Array.from(docIDSet).map((docID) => {
        const alerts = this.response.alerts.conditions.filter(
          (alert) => alert.relatedTo.id === docID,
        );

        return {
          id: docID,
          resource: alerts[0].relatedTo.resource,
          alerts,
          record: {
            data: null,
            type: null,
          },
        };
      });
    },
    finalizedAlert() {
      return this.response.alerts.records
        .map((item) => {
          return item.alerts
            .map((alert) => {
              const field = alert.condition.field,
                fieldDisplay = alert.condition.fieldDisplay,
                valueDisplay = alert.condition.valueDisplay,
                reminder = alert.reminder;
              let currentValue = item.data[field],
                conditionValue = alert.condition.value,
                record = "",
                currentValueDisplay = "",
                formattedDateAndTime = "",
                complete = false;

              switch (item.type.type) {
                case "tasks":
                  record = `${item.data.notes} | Task`;
                  break;
                case "salesOrders":
                  record = `${this.$options.filters.capitalize(
                    item.data.account.name,
                  )}, ${String(
                    item.data.orderNumber.internal,
                  ).toUpperCase()} | Sales Order`;
                  break;
              }

              switch (field) {
                case "reminder":
                  currentValueDisplay = moment(this.dateAndTime).format(
                    "MM/DD/YYYY @ hh:mm:ss A",
                  );
                  conditionValue = moment(alert.condition.valueDisplay, [
                    "MM/DD/YYYY [@] hh:mm A",
                  ]);
                  if (moment(this.dateAndTime).isSameOrAfter(conditionValue)) {
                    complete = true;
                  }
                  break;
                case "status":
                  switch (item.type.type) {
                    case "tasks":
                      currentValueDisplay = this.getTaskStatus.find(
                        (option) => option.value === currentValue,
                      ).text;
                      break;
                    case "salesOrders":
                      currentValueDisplay = this.getOrderStatus.find(
                        (option) => option.value === currentValue,
                      ).text;
                      break;
                  }
                  break;
                case "balanceDue":
                  switch (item.type.type) {
                    case "salesOrders":
                      currentValueDisplay = this.$options.filters.currency(
                        item.data.balance.due,
                      );
                      break;
                  }
                  break;
              }

              return {
                id: alert.id,
                record,
                created: alert.created,
                complete: alert.flags.complete || complete,
                completed: alert.completed,
                relatedTo: alert.relatedTo,
                notes: alert.notes,
                condition: {
                  fieldDisplay,
                  field,
                  currentValue,
                  valueDisplay,
                  currentValueDisplay,
                  conditionValue,
                },
              };
            })
            .flat();
        })
        .flat();
    },
    completedAlerts() {
      return this.finalizedAlert.filter((alert) => alert.complete);
    },
    inprogressAlerts() {
      return this.finalizedAlert.filter((alert) => !alert.complete);
    },
    alertHeaders() {
      let headers = [];

      switch (this.activeTab.alerts) {
        case 0:
          headers = [
            {
              text: "#",
              value: "number",
              sortable: false,
            },
            {
              text: "Record",
              value: "record",
              sortable: false,
            },
            {
              text: "Condition",
              value: "watcher",
              sortable: false,
            },
            {
              text: "Notes",
              value: "notes",
              sortable: false,
            },
            {
              text: "Complete?",
              value: "complete",
              sortable: true,
            },
            {
              text: "View",
              value: "view",
              sortable: false,
            },
            {
              text: "Completed",
              value: "completed",
              sortable: true,
            },
          ];
          break;
        case 1:
          headers = [
            {
              text: "#",
              value: "number",
              sortable: false,
            },
            {
              text: "Record",
              value: "record",
              sortable: false,
            },
            {
              text: "Condition",
              value: "watcher",
              sortable: false,
            },
            {
              text: "Notes",
              value: "notes",
              sortable: false,
            },
            {
              text: "Remove",
              value: "delete",
              sortable: false,
            },
            {
              text: "Completed",
              value: "completed",
              sortable: true,
            },
          ];
          break;
        case 2:
          headers = [
            {
              text: "#",
              value: "number",
              sortable: false,
            },
            {
              text: "Record",
              value: "record",
              sortable: false,
            },
            {
              text: "Notes",
              value: "notes",
              sortable: false,
            },
            {
              text: "Condition",
              value: "watcher",
              sortable: false,
            },
            {
              text: "Remove",
              value: "delete",
              sortable: false,
            },
          ];
          break;
      }

      return headers;
    },
    alerts() {
      switch (this.activeTab.alerts) {
        case 0:
          return this.finalizedAlert;
          break;
        case 1:
          return this.completedAlerts;
          break;
        case 2:
          return this.inprogressAlerts;
          break;
      }
    },
    isMac() {
      if (navigator.userAgentData?.platform?.length) {
        // Use userAgentData if available
        const platform = navigator.userAgentData.platform;
        return platform.toLowerCase().includes("mac");
      } else {
        // Fallback to userAgent if userAgentData is not supported
        const userAgent = navigator.userAgent.toLowerCase();
        return userAgent.includes("macintosh");
      }
    },
    metaKey() {
      let cmd = {
        name: "Command",
        abbreviation: "Cmd",
        icon: "mdi-apple-keyboard-command",
        text: "⌘",
        key: "meta",
      };
      let ctrl = {
        name: "Control",
        abbreviation: "Ctrl",
        icon: "mdi-apple-keyboard-control",
        text: "^",
        key: "control",
      };
      return this.isMac ? cmd : ctrl;
    },
    keyboardShortcuts() {
      let navigationSearch = {
        text: "Open/Close Navigation",
        keys: [this.metaKey, "/"],
      };
      let accountSearch = {
        text: "Find Account",
        keys: [this.metaKey, "K"],
      };
      let themeToggle = {
        text: "Toggle Light/Dark Mode",
        keys: [this.metaKey, "D"],
      };
      return { navigationSearch, accountSearch, themeToggle };
    },
    keyboardShortcutsArray() {
      return Object.values(this.keyboardShortcuts);
    },
    isPWA() {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone ||
        false
      );
    },
    bottomNavHeight() {
      return !this.isPWA ? "env(safe-area-inset-right)" : "56px";
    },
    getBottomSafeArea() {
      let iosSafeAreaHeight = () => {
        if (!window.visualViewport) return 0;

        const viewportHeight = window.visualViewport.height;
        const innerHeight = window.innerHeight;

        return innerHeight - viewportHeight;
      };

      let getCSSBottomSafeArea = () => {
        return parseInt(
          getComputedStyle(document.documentElement).getPropertyValue(
            "env(safe-area-inset-bottom)",
          ) || 0,
          10,
        );
      };
      const viewportInset = iosSafeAreaHeight();
      const cssInset = getCSSBottomSafeArea();

      return Math.max(viewportInset, cssInset);
    },
  },
};
</script>

<style scoped>
html,
body {
  margin: 0;
  padding: 0;
}

.v-text-field--filled.v-input--dense.v-text-field--single-line
  > .v-input__control
  > .v-input__slot {
  padding-left: 12px;
  padding-right: 12px;
}
.v-menu__content.theme--light.v-menu__content--auto.v-menu__content--fixed.menuable__content__active {
  top: 12px !important;
  right: 12px !important;
}

.v-navigation-drawer {
  z-index: 21 !important;
}

.v-navigation-drawer--bottom.v-navigation-drawer--is-mobile {
  max-height: 70% !important;
}

.notificationMenu {
  max-height: 350px !important;
  border-radius: 8px !important;
}

.v-small-dialog__activator >>> .v-small-dialog__activator__content {
  display: block !important;
}

.blurred-dialog {
  border-radius: 16px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(6.6px);
  -webkit-backdrop-filter: blur(6.6px);
  border: 1px solid rgba(255, 255, 255, 0.77);
}

.nav-search >>> .v-input__control {
  transition: border 0.3s ease !important; /* Smooth transition for the border */
  border: 1px solid transparent; /* Ensure there's always a border */
}
.nav-search.v-input--is-focused >>> .v-input__control {
  border: 1px solid rgb(69, 187, 255) !important;
  box-shadow: 0 0 2px rgba(0, 0, 255, 0.5); /* Optional shadow for focus effect */
}
.main-nav-bar >>> .v-toolbar__content {
  padding: 6px 8px !important;
}
.main-nav-bar.mobile >>> .v-toolbar__content {
  padding: 6px 0px !important;
}
.algolia-search >>> .v-input__slot {
  padding: 0px 16px !important;
}
.algolia-search >>> .v-text-field__slot {
  align-items: center !important;
  min-height: 100%;
}
.algolia-search >>> * {
  cursor: pointer !important;
}
.algolia-search >>> .v-text-field__slot > label {
  display: flex;
  align-items: center;
  min-height: fit-content !important;
  position: unset !important;
  min-width: fit-content !important;
  padding: 4px 0px !important;
}
</style>

<style>
/* CSS */
:root {
  font-family: Inter, sans-serif;
  font-feature-settings: "liga" 1, "calt" 1; /* fix for Chrome */
}
@supports (font-variation-settings: normal) {
  :root {
    font-family: InterVariable, sans-serif;
  }
}
.ais-Pagination-list {
  padding-left: 0 !important;
}
@media only screen and (max-width: 600px) {
  .v-dialog--active {
    margin: 12px !important;
  }
}
.v-tooltip__content {
  background: rgba(97, 97, 97, 1) !important;
}

.v-tooltip__content.menuable__content__active {
  opacity: 1 !important;
}
div.v-date-picker-table.v-date-picker-table--date {
  max-height: fit-content;
}

.highlighted-text {
  background: rgb(235, 235, 235);
}

.softShadow {
  box-shadow: rgba(149, 157, 165, 0.4) 0px 8px 8px !important;
}

.theme--dark .softShadow {
  box-shadow: rgba(0, 0, 0, 0.4) 0px 8px 8px !important;
}

.justify-evenly {
  justify-content: space-evenly !important;
}
/* 
.secondaryBorder {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.secondaryBorder:hover {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
}

.secondaryBorder:focus {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
}

.secondaryBorder:active {
  border: 1px solid rgba(0, 0, 0, 0.36) !important;
}

.secondaryBorder:focus-within {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
}

.secondaryBorder:focus-visible {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
} */
</style>
